import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";
import { Textarea } from "./components/ui/textarea";
// Removed Select components to fix RovingFocusGroupItem error
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import StaffChat from "./components/Chat/StaffChat";
import AdminChatDashboard from "./components/Chat/AdminChatDashboard";
import JobTemplateManager from "./components/JobTemplates/JobTemplateManager";
import JobPhotoUploader from "./components/JobPhotos/JobPhotoUploader";
import StaffAvailabilityCalendar from "./components/Availability/StaffAvailabilityCalendar";
import StaffSelector from "./components/StaffSelector/StaffSelector";
import StaffQuickStats from "./components/Stats/StaffQuickStats";
import StaffPerformanceDashboard from "./components/Stats/StaffPerformanceDashboard";
import MessageClientButton from "./components/Messaging/MessageClientButton";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ImportBookings from "./components/Import/ImportBookings";
import PayRun from "./components/Pay/PayRun";
import MyPay from "./components/Pay/MyPay";
import ProfitPerJob from "./components/Profit/ProfitPerJob";
import CocktailChaseList from "./components/Cocktails/CocktailChaseList";
import ProfitSettings from "./components/Settings/ProfitSettings";
import StaffAccountsPanel from "./components/Staff/StaffAccountsPanel";
import HoldingZone from "./components/Jobs/HoldingZone";
import ArchivedJobs from "./components/Jobs/ArchivedJobs";
import BookingTextButton, { BookingTextHistory } from "./components/Jobs/BookingTextButton";
import MyAvailability from "./components/Availability/MyAvailability";
import JobAvailability from "./components/Availability/JobAvailability";
import JobCocktailStatus from "./components/Cocktails/JobCocktailStatus";
import CocktailSubmissions from "./components/Cocktails/CocktailSubmissions";
import ForgotPasswordDialog from "./components/Auth/ForgotPasswordDialog";
import ResetPasswordPage from "./components/Auth/ResetPasswordPage";
import PendingApproval from "./components/Auth/PendingApproval";
import CredentialsCard from "./components/Profile/CredentialsCard";
import TravelInfo from "./components/Travel/TravelInfo";
import AdminJobTravel from "./components/Travel/AdminJobTravel";
import TravelSettings from "./components/Travel/TravelSettings";
import HomePostcodeReminder from "./components/Travel/HomePostcodeReminder";
import BrandTopBar from "./components/Brand/BrandTopBar";
import BrandAuthLayout from "./components/Brand/BrandAuthLayout";
import BrandLoading, { SkeletonCards } from "./components/Brand/BrandLoading";
import OrderChip from "./components/Brand/OrderChip";
import NextJobCard from "./components/Jobs/NextJobCard";
import EmptyState from "./components/ui/empty-state";
import { UKDatePicker } from "./components/ui/date-picker-uk";
import EmailSettings from "./components/Settings/EmailSettings";
import JobEmailPanel from "./components/Emails/JobEmailPanel";
import JobRequestsPanel from "./components/Jobs/JobRequestsPanel";
import RatePage from "./components/Ratings/RatePage";
import MyRatingCard from "./components/Staff/MyRatingCard";
import JobDayActions from "./components/Jobs/JobDayActions";
import TodayPanel from "./components/Jobs/TodayPanel";
import { ukDate, ukDateLong, ukTime, money as gbp, firstName } from "./lib/format";
import { initSimpleNotifications, notifyNewJob, notifyNewMessage } from "./utils/pushNotifications";
import { 
  CheckCircle, 
  Phone, 
  MapPin, 
  Clock, 
  User, 
  Calendar,
  Briefcase,
  Archive,
  CalendarClock,
  Settings,
  LogOut,
  Shield,
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Users,
  MessageCircle,
  BarChart3,
  RefreshCw,
  Eye,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  List,
  Camera,
  Image,
  Download,
  DollarSign,
  Bell,
  BellRing,
  Send,
  Mail,
  X,
  Circle,
  Navigation,
  Upload,
  FileText,
  TrendingUp,
  FileSpreadsheet,
  AlertTriangle,
  PoundSterling,
  Martini
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // Always start with null
  const [loading, setLoading] = useState(false); // No loading needed

  // Clear any existing authentication on app start
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const Login = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    emergency_contact: "",
    home_address: "",
    can_drive: null,
    services_provided: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });

      login(response.data.token, response.data.user);
      
      // Staff login page - reject admin users
      if (response.data.user.role === 'admin') {
        setError("Admin access not allowed here. Please use the admin portal.");
        return;
      }
      
      // Only staff users can proceed
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Login error:", error.response?.data);
      setError(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(`${API}/auth/staff-register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone || null,
        emergency_contact: formData.emergency_contact || null,
        home_address: formData.home_address || null,
        can_drive: formData.can_drive
      });

      login(response.data.token, response.data.user);
      setSuccess("Thanks! Your application has been sent to the office for review.");
      
      // Auto-login after successful registration
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    } catch (error) {
      console.error("Registration error:", error.response?.data);
      setError(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError("");
    setSuccess("");
    // Reset form when switching modes
    setFormData({
      email: "",
      password: "",
      name: "",
      phone: "",
      emergency_contact: "",
      home_address: "",
      can_drive: null,
      services_provided: []
    });
  };

  return (
    <BrandAuthLayout
      strapline={isRegistering ? "Join our team" : "Staff portal"}
      testId="staff-login-page"
    >
      <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
        <div>
          <h1 className="text-lg text-cream">{isRegistering ? "Join our team" : "Welcome back"}</h1>
          <p className="mt-1 text-sm normal-case text-cream/60" style={{ textTransform: 'none' }}>
            {isRegistering
              ? "Tell us a little about you and the office will be in touch."
              : "Sign in to see your jobs, pay and messages."}
          </p>
        </div>

        <div>
          <label htmlFor="login-email" className="bd-label-dark">Email address</label>
          <Input
            id="login-email"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            className="h-12 bd-input-dark"
            data-testid="login-email-input"
          />
        </div>

        <div>
          <label htmlFor="login-password" className="bd-label-dark">Password</label>
          <Input
            id="login-password"
            type="password"
            placeholder="Your password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            required
            className="h-12 bd-input-dark"
            data-testid="login-password-input"
          />
        </div>

        {isRegistering && (
          <>
            <div>
              <label htmlFor="join-name" className="bd-label-dark">Full name</label>
              <Input
                id="join-name"
                type="text"
                placeholder="First and last name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                className="h-12 bd-input-dark"
                data-testid="join-name-input"
              />
            </div>

            <div>
              <label htmlFor="join-phone" className="bd-label-dark">Mobile number</label>
              <Input
                id="join-phone"
                type="tel"
                placeholder="07700 900000"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="h-12 bd-input-dark"
                data-testid="join-phone-input"
              />
            </div>

            <div>
              <label htmlFor="join-emergency" className="bd-label-dark">Emergency contact number</label>
              <Input
                id="join-emergency"
                type="tel"
                placeholder="Someone we can call"
                value={formData.emergency_contact}
                onChange={(e) => handleInputChange('emergency_contact', e.target.value)}
                className="h-12 bd-input-dark"
                data-testid="join-emergency-input"
              />
            </div>

            <div>
              <label htmlFor="join-address" className="bd-label-dark">Home address</label>
              <Input
                id="join-address"
                type="text"
                placeholder="Street and town"
                value={formData.home_address}
                onChange={(e) => handleInputChange('home_address', e.target.value)}
                className="h-12 bd-input-dark"
                data-testid="join-address-input"
              />
            </div>

            <div className="space-y-2">
              <span className="bd-label-dark">Can you drive?</span>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={formData.can_drive === true ? "default" : "outline"}
                  onClick={() => handleInputChange('can_drive', true)}
                  className={`flex-1 ${formData.can_drive === true ? '' : 'bg-transparent text-cream border-gold/30 hover:bg-white/10 hover:text-cream'}`}
                  data-testid="join-can-drive-yes"
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={formData.can_drive === false ? "default" : "outline"}
                  onClick={() => handleInputChange('can_drive', false)}
                  className={`flex-1 ${formData.can_drive === false ? '' : 'bg-transparent text-cream border-gold/30 hover:bg-white/10 hover:text-cream'}`}
                  data-testid="join-can-drive-no"
                >
                  No
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <span className="bd-label-dark">Services you can provide</span>
              <div className="grid grid-cols-1 gap-2">
                {['butler in the buff', 'Life Model', 'Cocktail Tutor', 'Stripper'].map((service) => (
                  <label
                    key={service}
                    className="flex min-h-11 items-center gap-3 rounded-[10px] border border-gold/20 bg-white/5 px-3 transition-colors hover:border-gold/50"
                  >
                    <input
                      type="checkbox"
                      checked={formData.services_provided.some((s) => s.toLowerCase() === service.toLowerCase())}
                      onChange={(e) => {
                        const services = formData.services_provided || [];
                        if (e.target.checked) {
                          handleInputChange('services_provided', [...services, service]);
                        } else {
                          handleInputChange('services_provided', services.filter(s => s !== service));
                        }
                      }}
                      className="h-4 w-4 rounded border-gold/40 accent-[#E0C178]"
                    />
                    <span className="text-sm text-cream/85">{service}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-cream/45">Choose everything you are comfortable providing.</p>
            </div>
          </>
        )}

        {error && (
          <p className="rounded-[10px] border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="login-error">
            {error}
          </p>
        )}

        {success && (
          <p className="rounded-[10px] border border-gold/40 bg-gold/10 px-3 py-2 text-sm text-gold" data-testid="login-success">
            {success}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading}
          size="lg"
          className="w-full"
          data-testid="login-submit-btn"
        >
          {loading
            ? (isRegistering ? "Sending your application…" : "Signing you in…")
            : (isRegistering ? "Send my application" : "Sign in")}
        </Button>

        {!isRegistering && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-cream/60 underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold"
              data-testid="staff-forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
        )}

        <div className="border-t border-gold/15 pt-4 text-center">
          <button
            type="button"
            onClick={toggleMode}
            className="text-sm font-semibold text-gold transition-colors hover:text-cream"
            data-testid="login-toggle-mode"
          >
            {isRegistering ? "Already have an account? Sign in" : "New to Butler Direct? Join our team"}
          </button>
        </div>
      </form>

      <ForgotPasswordDialog open={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
    </BrandAuthLayout>
  );
};

// Notification System
const NotificationCenter = ({ token }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setPermissionGranted(permission === 'granted');
      
      if (permission === 'granted') {
        // Subscribe to notifications
        await subscribeToNotifications();
        showBrowserNotification('🔔 Notifications Enabled', 'You\'ll now receive job alerts!');
      }
    }
  };

  // Subscribe to push notifications
  const subscribeToNotifications = async () => {
    try {
      await axios.post(`${API}/notifications/subscribe`, {
        endpoint: 'browser-notification',
        keys: { demo: true }
      }, axiosConfig);
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, axiosConfig);
      setNotifications(response.data);
      setUnreadCount(response.data.filter(notif => !notif.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Show browser notification
  const showBrowserNotification = (title, message) => {
    if (permissionGranted && 'Notification' in window) {
      new Notification(title, {
        body: message,
        icon: '/butler-direct-logo.png',
        badge: '/butler-direct-logo.png',
        tag: 'butler-direct-notification'
      });
    }
  };

  // Simulate receiving notifications (in real app, this would come from push service)

  useEffect(() => {
    // Check if notifications are already permitted
    if ('Notification' in window) {
      setPermissionGranted(Notification.permission === 'granted');
    }

    fetchNotifications();
    const timer = setInterval(fetchNotifications, 60000);
    const refresh = () => { if (!document.hidden) fetchNotifications(); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
    /* eslint-disable-next-line */
  }, []);

  const markAsRead = async (notificationId) => {
    const notification = notifications.find(notif => notif.id === notificationId);
    if (!notification || notification.read) return;

    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await axios.post(`${API}/notifications/${notificationId}/mark-read`, {}, axiosConfig);
    } catch (error) {
      fetchNotifications();
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job_available': return '🚨';
      case 'job_assigned': return '✅';
      case 'reminder': return '⏰';
      default: return '📢';
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-xs bg-red-500 text-white rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <Card className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto z-50 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(false)}
              >
                ✕
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-3">
            {/* Enable Notifications */}
            {!permissionGranted && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 mb-2">
                  Enable notifications to get job alerts!
                </p>
                <Button
                  size="sm"
                  onClick={requestNotificationPermission}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white"
                >
                  <BellRing className="w-3 h-3 mr-1" />
                  Enable Alerts
                </Button>
              </div>
            )}

            {/* Notifications List */}
            {notifications.length === 0 ? (
              <div className="text-center py-4">
                <Bell className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    notification.read 
                      ? 'bg-slate-50 border-slate-200' 
                      : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(notification.sent_at).toLocaleString()}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Calendar Component
const CalendarView = ({ jobs, onCheckIn, onContactClient, token }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState(null);

  // Get calendar data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  // Get first day of month and days in month
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

  // Create array of calendar days
  const calendarDays = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Get jobs for a specific day
  const getJobsForDay = (day) => {
    if (!day) return [];
    const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return jobs.filter(job => job.date === dayStr);
  };

  // Navigate months
  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <Card className="bg-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Job Calendar
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">View your jobs in calendar format</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center min-w-[140px]">
                <h3 className="font-semibold text-lg">
                  {monthNames[month]} {year}
                </h3>
              </div>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {dayNames.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 bg-slate-50 rounded">
                {day}
              </div>
            ))}
            
            {/* Calendar Days */}
            {calendarDays.map((day, index) => {
              const dayJobs = getJobsForDay(day);
              const isToday = day && new Date().toDateString() === new Date(year, month, day).toDateString();
              
              return (
                <div
                  key={index}
                  className={`min-h-[80px] p-1 border border-slate-200 rounded ${
                    day ? 'bg-white hover:bg-slate-50' : 'bg-slate-50'
                  } ${isToday ? 'ring-2 ring-yellow-400' : ''}`}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${
                        isToday ? 'text-yellow-600' : 'text-slate-700'
                      }`}>
                        {day}
                      </div>
                      
                      {/* Jobs for this day */}
                      <div className="space-y-1">
                        {dayJobs.slice(0, 2).map((job, jobIndex) => (
                          <div
                            key={jobIndex}
                            onClick={() => setSelectedJob(job)}
                            className="text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                              backgroundColor: job.job_type === 'Cocktail Lesson' ? '#fef3c7' :
                                             job.job_type === 'Butler Service' ? '#e0e7ff' :
                                             job.job_type === 'The Artistic Package' ? '#f3e8ff' :
                                             job.job_type === 'Late Night Package' ? '#fef2f2' :
                                             job.job_type === 'The Ultimate Package' ? '#f0fdf4' :
                                             '#f3f4f6',
                              color: job.job_type === 'Cocktail Lesson' ? '#92400e' :
                                     job.job_type === 'Butler Service' ? '#3730a3' :
                                     job.job_type === 'The Artistic Package' ? '#7c3aed' :
                                     job.job_type === 'Late Night Package' ? '#dc2626' :
                                     job.job_type === 'The Ultimate Package' ? '#16a34a' :
                                     '#374151'
                            }}
                          >
                            <div className="font-medium truncate">
                              {job.job_type}
                            </div>
                            <div className="truncate">
                              {job.time} • {job.location}
                            </div>
                          </div>
                        ))}
                        
                        {dayJobs.length > 2 && (
                          <div className="text-xs text-slate-500 text-center">
                            +{dayJobs.length - 2} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Job Details Modal */}
      {selectedJob && (
        <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                {selectedJob.job_type}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-sm">
                  {ukDate(selectedJob.date)}{selectedJob.time ? ` · ${ukTime(selectedJob.time) || selectedJob.time}` : ''}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-500 italic">Client details protected</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-500 italic">Phone available on job day</span>
              </div>
              
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                <span className="text-sm">{selectedJob.location}</span>
              </div>

              {token && <TravelInfo jobId={selectedJob.id} token={token} />}

              {selectedJob.equipment_needed && (
                <div className="flex items-start gap-2">
                  <Briefcase className="w-4 h-4 text-slate-500 mt-0.5" />
                  <span className="text-sm">{selectedJob.equipment_needed}</span>
                </div>
              )}

              {selectedJob.cocktails_selected && selectedJob.cocktails_selected.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Cocktails Selected:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedJob.cocktails_selected.map((cocktail, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {cocktail}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    onCheckIn(selectedJob.id);
                    setSelectedJob(null);
                  }}
                  disabled={selectedJob.check_in_confirmed}
                  className={`flex-1 ${
                    selectedJob.check_in_confirmed 
                      ? "bg-green-100 text-green-800 cursor-not-allowed" 
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {selectedJob.check_in_confirmed ? "Checked In" : "Check In"}
                </Button>
                
                <Button
                  onClick={() => {
                    onContactClient(selectedJob.id);
                    setSelectedJob(null);
                  }}
                  disabled={selectedJob.client_contacted}
                  variant={selectedJob.client_contacted ? "secondary" : "outline"}
                  className={`flex-1 ${
                    selectedJob.client_contacted 
                      ? "bg-blue-100 text-blue-800 cursor-not-allowed" 
                      : "border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {selectedJob.client_contacted ? "Contacted" : "Contact Client"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Image Upload Component
const ImageUpload = ({ jobId, token, onUploadComplete }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [description, setDescription] = useState('');

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/images`, axiosConfig);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [jobId]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        await axios.post(`${API}/jobs/${jobId}/upload-image`, {
          job_id: jobId,
          image_data: base64Data,
          description: description
        }, axiosConfig);

        setDescription('');
        setShowUpload(false);
        fetchImages();
        if (onUploadComplete) onUploadComplete();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/jobs/${jobId}/images/${imageId}`, axiosConfig);
      fetchImages();
      if (onUploadComplete) onUploadComplete();
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-medium text-slate-700">
            Job Photos ({images.length})
          </span>
          {images.length > 0 && (
            <Badge className="bg-green-100 text-green-800 text-xs">
              Bonus Eligible
            </Badge>
          )}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowUpload(!showUpload)}
          disabled={uploading}
          className="text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Photo
        </Button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <Card className="p-3 bg-slate-50">
          <div className="space-y-3">
            <div>
              <Input
                type="text"
                placeholder="Photo description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="flex-1 text-sm"
                id={`file-upload-${jobId}`}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUpload(false)}
                className="text-xs"
              >
                Cancel
              </Button>
            </div>
            {uploading && (
              <p className="text-xs text-blue-600">Uploading...</p>
            )}
          </div>
        </Card>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <img
                src={image.image_url}
                alt={image.description || 'Job photo'}
                className="w-full h-20 object-cover rounded border"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity rounded flex items-center justify-center">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteImage(image.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              {image.description && (
                <p className="text-xs text-slate-500 mt-1 truncate">
                  {image.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && !showUpload && (
        <div className="text-center py-3">
          <Camera className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-xs text-slate-500">No photos uploaded yet</p>
          <p className="text-xs text-slate-400">Upload photos to earn bonus!</p>
        </div>
      )}
    </div>
  );
};

// Calendar Dropdown Component
const CalendarDropdown = ({ job, onDownloadICS, onGoogleCalendar, onOutlookCalendar }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="outline"
        size="sm"
        className="text-xs bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 px-2 sm:px-3"
      >
        <Calendar className="w-3 h-3" />
        <span className="hidden sm:inline ml-1">Add to Calendar</span>
        <span className="sm:hidden ml-1">Cal</span>
        <ChevronDown className="w-3 h-3 ml-1" />
      </Button>
      
      {isOpen && (
        <div className="absolute right-0 mt-1 w-40 sm:w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[9999]">
          <div className="py-1">
            <button
              onClick={() => {
                onDownloadICS(job);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Download .ics file</span>
              <span className="sm:hidden">Download</span>
            </button>
            <button
              onClick={() => {
                onGoogleCalendar(job);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Add to Google Calendar</span>
              <span className="sm:hidden">Google</span>
            </button>
            <button
              onClick={() => {
                onOutlookCalendar(job);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Add to Outlook Calendar</span>
              <span className="sm:hidden">Outlook</span>
            </button>
          </div>
        </div>
      )}
      
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Job Card Component (Staff View)
// Job Card Component (Staff View)
const JobCard = ({ job, onCheckIn, onContactClient, token, shouldShowClientDetails, user, onDownloadICS, onGoogleCalendar, onOutlookCalendar }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const isToday = new Date(job.date).toDateString() === new Date().toDateString();
  
  const handleUploadComplete = () => {
    setRefreshKey(prev => prev + 1);
  };
  
  return (
    <Card className="mb-4 border-l-[3px] border-l-gold bg-white transition-shadow duration-300 hover:shadow-gold" data-testid={`job-card-${job.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base text-ink">
              {job.job_type}
            </CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={isToday ? "default" : "secondary"} className="text-xs">
                {isToday ? "Today" : ukDate(job.date)}
              </Badge>
              {job.time && (
                <Badge variant="outline" className="text-xs">
                  {ukTime(job.time) || job.time}
                </Badge>
              )}
              <OrderChip value={job.order_number || job.job_number} fallback="" testId={`job-order-${job.id}`} />
              {job.image_count > 0 && (
                <Badge className="bg-green-100 text-green-800 text-xs">
                  {job.image_count} photo{job.image_count > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {job.check_in_confirmed && (
              <Badge className="bg-green-100 text-green-800 text-xs">
                Checked in
              </Badge>
            )}
            {job.client_contacted && (
              <Badge variant="secondary" className="text-xs">
                Client contacted
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Client details - only show if assigned and within 1 week */}
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <User className="w-4 h-4" />
          <span className="font-medium">
            {shouldShowClientDetails(job) ? job.client_name : 
             job.status === 'assigned' ? 'Client details available 1 week before event' :
             'Client details available after assignment'}
          </span>
        </div>
        
        {shouldShowClientDetails(job) ? (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="w-4 h-4" />
            <span>{job.client_phone}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate-400 italic">
            <Phone className="w-4 h-4" />
            <span>Contact details protected</span>
          </div>
        )}
        
        <div className="flex items-start gap-2 text-sm text-slate-600">
          <MapPin className="w-4 h-4 mt-0.5" />
          <span>{job.location}</span>
        </div>

        {/* Travel distance and travel money */}
        <TravelInfo jobId={job.id} token={token} />

        {job.equipment_needed && (
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <Briefcase className="w-4 h-4 mt-0.5" />
            <span>{job.equipment_needed}</span>
          </div>
        )}

        {job.status !== 'completed' && <JobDayActions job={job} token={token} />}

        {/* Financial Information */}
        {(job.wage || job.travel_allowance || job.bonus) && (
          <div className="rounded-[10px] border border-hair bg-cream p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink">What you'll earn</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              {job.wage && (
                <div className="text-center">
                  <p className="bd-money font-bold text-ink">{gbp(job.wage)}</p>
                  <p className="text-xs text-slate-500">Wage</p>
                </div>
              )}
              {job.travel_allowance && (
                <div className="text-center">
                  <p className="bd-money font-bold text-ink">{gbp(job.travel_allowance)}</p>
                  <p className="text-xs text-slate-500">Travel</p>
                </div>
              )}
              {job.bonus && (
                <div className="text-center">
                  <p className="bd-money font-bold text-gold-deep">{gbp(job.bonus)}</p>
                  <p className="text-xs text-slate-500">Bonus</p>
                </div>
              )}
            </div>
            {(job.wage && job.travel_allowance) && (
              <div className="mt-2 pt-2 border-t border-green-300">
                <p className="text-center font-semibold text-green-800">
                  Total: £{(parseFloat(job.wage || 0) + parseFloat(job.travel_allowance || 0) + parseFloat(job.bonus || 0)).toFixed(2)}
                </p>
              </div>
            )}
          </div>
        )}
        {job.cocktails_selected && job.cocktails_selected.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-medium text-slate-700 mb-2">Cocktails Selected:</p>
            <div className="flex flex-wrap gap-1">
              {job.cocktails_selected.map((cocktail, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {cocktail}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Team Members Section - only show if multiple staff assigned */}
        {job.assigned_staff_details && job.assigned_staff_details.length > 1 && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-800 mb-2">
              Your team on this job
            </p>
            <div className="space-y-2">
              {job.assigned_staff_details.map((staff, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-blue-800">{staff.name}</span>
                  <div className="text-xs text-blue-600">
                    {staff.email}
                    {staff.phone && (
                      <>
                        <br />
                        <span className="font-medium">{staff.phone}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-blue-600 mt-2 italic">
              Contact your team members to coordinate the service.
            </p>
          </div>
        )}
        <Separator className="my-3" />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-1">
            <Button
              onClick={() => onCheckIn(job.id)}
              disabled={job.check_in_confirmed}
              className={`flex-1 text-xs sm:text-sm ${
                job.check_in_confirmed 
                  ? "bg-green-100 text-green-800 cursor-not-allowed" 
                  : "bg-slate-900 hover:bg-slate-800 text-white"
              }`}
            >
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{job.check_in_confirmed ? "Checked In" : "Check In"}</span>
              <span className="sm:hidden">{job.check_in_confirmed ? "✓" : "Check"}</span>
            </Button>
            
            <div className="flex-1">
              <MessageClientButton 
                job={job} 
                user={user} 
                shouldShowClientDetails={shouldShowClientDetails}
                compact={true}
                fullWidth={true}
              />
            </div>
          </div>
          
          <CalendarDropdown 
            job={job}
            onDownloadICS={onDownloadICS}
            onGoogleCalendar={onGoogleCalendar}
            onOutlookCalendar={onOutlookCalendar}
          />
        </div>

        {/* Contact Client Button */}
        <div className="flex gap-2 mt-2">
          <Button
            onClick={() => onContactClient(job.id)}
            disabled={job.client_contacted}
            variant={job.client_contacted ? "secondary" : "outline"}
            size="sm"
            className={`text-xs sm:text-sm ${
              job.client_contacted 
                ? "bg-blue-100 text-blue-800 cursor-not-allowed" 
                : "border-slate-300 hover:bg-slate-50"
            }`}
          >
            <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{job.client_contacted ? "Client Contacted" : "Mark Client Contacted"}</span>
            <span className="sm:hidden">{job.client_contacted ? "Contacted" : "Mark Contacted"}</span>
          </Button>
        </div>

        <Separator className="my-3" />

        {/* Image Upload Section */}
        <ImageUpload 
          key={refreshKey}
          jobId={job.id} 
          token={token}
          onUploadComplete={handleUploadComplete}
        />
      </CardContent>
    </Card>
  );
};

// Staff Dashboard Component
const Dashboard = () => {
  const [myJobs, setMyJobs] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("my-jobs");
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [showPerformanceDashboard, setShowPerformanceDashboard] = useState(false);
  const { user, token, logout } = useAuth();

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchChatUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, axiosConfig);
      setChatUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching chat unread count:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const [myJobsRes, availableJobsRes, completedJobsRes] = await Promise.all([
        axios.get(`${API}/jobs/my-jobs`, axiosConfig),
        axios.get(`${API}/jobs/available`, axiosConfig),
        axios.get(`${API}/jobs/completed`, axiosConfig)
      ]);

      setMyJobs(myJobsRes.data);
      setAvailableJobs(availableJobsRes.data);
      setCompletedJobs(completedJobsRes.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchChatUnreadCount();
    
    // Initialize push notifications
    initSimpleNotifications();
    
    // Poll for new messages every 10 seconds
    const chatInterval = setInterval(fetchChatUnreadCount, 10000);
    return () => clearInterval(chatInterval);
  }, []);

  const handleCheckIn = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/check-in/${jobId}`, {}, axiosConfig);
      fetchJobs(); // Refresh jobs
    } catch (error) {
      console.error("Error checking in:", error);
    }
  };

  const handleContactClient = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/contact-client/${jobId}`, {}, axiosConfig);
      fetchJobs(); // Refresh jobs
    } catch (error) {
      console.error("Error contacting client:", error);
    }
  };

  const handleRequestJob = async (jobId) => {
    try {
      const response = await axios.post(`${API}/jobs/request/${jobId}`, {}, axiosConfig);
      alert("✅ " + response.data.message + "\n\nThe admin will review your request and notify you of their decision.");
      fetchJobs(); // Refresh jobs
    } catch (error) {
      console.error("Error requesting job:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send job request";
      alert("❌ " + errorMsg);
    }
  };

  // Helper function to check if client details should be shown
  const shouldShowClientDetails = (job) => {
    // Only show client details if job is assigned to current user
    const isAssignedToUser = job.assigned_staff_emails?.includes(user?.email) || 
                            job.assigned_staff_email === user?.email;
    
    console.log('🔍 Client Details Check:', {
      jobId: job.id,
      jobType: job.job_type,
      jobDate: job.date,
      userEmail: user?.email,
      assignedEmails: job.assigned_staff_emails,
      assignedEmail: job.assigned_staff_email,
      isAssignedToUser
    });
    
    if (!isAssignedToUser) {
      console.log('❌ Not assigned to user');
      return false;
    }
    
    const jobDate = new Date(job.date);
    const now = new Date();
    const oneWeekBefore = new Date(jobDate);
    oneWeekBefore.setDate(jobDate.getDate() - 7);
    
    const shouldShow = now >= oneWeekBefore;
    
    console.log('📅 Date Comparison:', {
      jobDate: jobDate.toISOString(),
      now: now.toISOString(),
      oneWeekBefore: oneWeekBefore.toISOString(),
      shouldShow,
      daysDifference: Math.ceil((jobDate - now) / (1000 * 60 * 60 * 24))
    });
    
    // Show details if we are within 1 week before the job OR if the job date has passed
    // This ensures details are available 1 week before and NEVER hidden again after that
    return shouldShow;
  };

  // Calendar integration utilities
  const generateCalendarEvent = (job) => {
    // Safely handle time parsing
    let timeValue = job.time || '19:00';
    if (!timeValue || timeValue === 'null' || timeValue === 'undefined') {
      timeValue = '19:00';
    }
    
    const startDate = new Date(`${job.date}T${timeValue}:00`);
    
    // Check if date is valid
    if (isNaN(startDate.getTime())) {
      console.error('Invalid date/time for job:', job);
      // Use fallback date
      const fallbackDate = new Date();
      fallbackDate.setHours(19, 0, 0, 0);
      const endDate = new Date(fallbackDate.getTime() + 3 * 60 * 60 * 1000);
      return {
        title: `${job.job_type} - ${job.location}`,
        description: `Butler Direct Job\nType: ${job.job_type}\nLocation: ${job.location}\nJob #: ${job.job_number || 'N/A'}\n\nFor questions, contact Butler Direct.`,
        location: job.location,
        startDate: fallbackDate,
        endDate: endDate
      };
    }
    
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // 3 hours duration
    
    const title = `${job.job_type} - ${job.location}`;
    const description = `Butler Direct Job\nType: ${job.job_type}\nLocation: ${job.location}\nJob #: ${job.job_number || 'N/A'}\n\nFor questions, contact Butler Direct.`;
    
    return {
      title,
      description,
      location: job.location,
      startDate,
      endDate
    };
  };

  const formatDateForCalendar = (date) => {
    try {
      if (!date || isNaN(date.getTime())) {
        console.error('Invalid date passed to formatDateForCalendar:', date);
        // Return current time as fallback
        return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    } catch (error) {
      console.error('Error formatting date for calendar:', error);
      return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    }
  };

  const downloadICSFile = (job) => {
    try {
      console.log('downloadICSFile called with job:', job.job_number);
      const event = generateCalendarEvent(job);
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Butler Direct//Staff Calendar//EN',
      'BEGIN:VEVENT',
      `DTSTART:${formatDateForCalendar(event.startDate)}`,
      `DTEND:${formatDateForCalendar(event.endDate)}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
      `LOCATION:${event.location}`,
      `UID:${job.id || Date.now()}@butlerdirect.co.uk`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `butler-direct-${job.job_number || 'job'}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('ICS file download initiated');
    } catch (error) {
      console.error('Error downloading ICS file:', error);
      alert('Failed to download calendar file. Please try again.');
    }
  };

  const addToGoogleCalendar = (job) => {
    try {
      console.log('addToGoogleCalendar called with job:', job.job_number);
      const event = generateCalendarEvent(job);
      const googleUrl = new URL('https://calendar.google.com/calendar/render');
      
      googleUrl.searchParams.set('action', 'TEMPLATE');
      googleUrl.searchParams.set('text', event.title);
      googleUrl.searchParams.set('dates', `${formatDateForCalendar(event.startDate)}/${formatDateForCalendar(event.endDate)}`);
      googleUrl.searchParams.set('details', event.description);
      googleUrl.searchParams.set('location', event.location);
      
      console.log('Opening Google Calendar with URL:', googleUrl.toString());
      window.open(googleUrl.toString(), '_blank');
    } catch (error) {
      console.error('Error adding to Google Calendar:', error);
      alert('Failed to open Google Calendar. Please try again.');
    }
  };

  const addToOutlookCalendar = (job) => {
    try {
      console.log('addToOutlookCalendar called with job:', job.job_number);
      const event = generateCalendarEvent(job);
      const outlookUrl = new URL('https://outlook.live.com/calendar/0/deeplink/compose');
      
      outlookUrl.searchParams.set('subject', event.title);
      outlookUrl.searchParams.set('startdt', event.startDate.toISOString());
      outlookUrl.searchParams.set('enddt', event.endDate.toISOString());
      outlookUrl.searchParams.set('body', event.description);
      outlookUrl.searchParams.set('location', event.location);
      
      console.log('Opening Outlook Calendar with URL:', outlookUrl.toString());
      window.open(outlookUrl.toString(), '_blank');
    } catch (error) {
      console.error('Error adding to Outlook Calendar:', error);
      alert('Failed to open Outlook Calendar. Please try again.');
    }
  };

  // Calculate distance from staff home to job location
  const calculateJobDistance = async (jobLocation) => {
    try {
      const response = await axios.post(`${API}/staff/calculate-job-distance`, {
        job_location: jobLocation
      }, axiosConfig);
      return response.data;
    } catch (error) {
      console.error("Error calculating job distance:", error);
      return null;
    }
  };

  if (loading) {
    return <BrandLoading line="Getting your jobs ready…" testId="staff-loading" />;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextJob = [...myJobs]
    .filter((job) => {
      const date = new Date(job.date);
      return !isNaN(date) && date >= today;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  return (
    <div className="min-h-screen bg-page">
      <BrandTopBar subtitle="Staff portal" testId="staff-top-bar">
        <NotificationCenter token={token} />
        <Button
          variant="outline"
          size="icon"
          onClick={logout}
          aria-label="Sign out"
          className="border-gold/30 bg-transparent text-cream hover:bg-white/10 hover:text-cream"
          data-testid="staff-logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </BrandTopBar>

      {/* Main Content */}
      <main className="mx-auto max-w-[1100px] p-4 pb-24">
        <div className="mb-5 bd-fade-up">
          <h1 className="text-2xl text-ink sm:text-3xl">Hi {firstName(user?.name)}</h1>
          <p className="mt-1 text-sm text-slate-500">{ukDateLong(new Date())}</p>
        </div>

        <HomePostcodeReminder token={token} onGoToProfile={() => setActiveTab("profile")} />

        {activeTab === "my-jobs" && <NextJobCard job={nextJob} token={token} />}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bd-tabs-dark mb-6 grid w-full grid-cols-6 gap-1 rounded-[14px] p-1.5 sm:grid-cols-7">
            <TabsTrigger value="my-jobs" className="flex min-h-11 flex-col items-center gap-0.5 text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm" data-testid="staff-tab-my-jobs">
              <Briefcase className="w-4 h-4" />
              <span>My jobs</span>
            </TabsTrigger>
            <TabsTrigger value="available" className="flex min-h-11 flex-col items-center gap-0.5 text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm" data-testid="staff-tab-available">
              <Calendar className="w-4 h-4" />
              <span>Available</span>
            </TabsTrigger>
            <TabsTrigger value="availability" className="flex min-h-11 flex-col items-center gap-0.5 text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm" data-testid="staff-tab-availability">
              <CalendarClock className="w-4 h-4" />
              <span>Availability</span>
            </TabsTrigger>
            <TabsTrigger value="my-pay" className="flex min-h-11 flex-col items-center gap-0.5 text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm" data-testid="staff-my-pay-tab">
              <PoundSterling className="w-4 h-4" />
              <span>My pay</span>
            </TabsTrigger>
            <TabsTrigger value="chat" className="relative flex min-h-11 flex-col items-center gap-0.5 text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm" data-testid="staff-tab-chat">
              <MessageCircle className="w-4 h-4" />
              <span>Chat</span>
              {chatUnreadCount > 0 && (
                <span className="absolute -top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                  {chatUnreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex min-h-11 flex-col items-center gap-0.5 text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm" data-testid="staff-tab-profile">
              <User className="w-4 h-4" />
              <span>Profile</span>
            </TabsTrigger>
            <TabsTrigger value="completed" className="hidden min-h-11 items-center gap-1.5 text-sm sm:flex" data-testid="staff-tab-completed">
              <CheckCircle className="w-4 h-4" />
              <span>Completed</span>
            </TabsTrigger>
          </TabsList>

          {/* Staff Quick Stats with Dashboard Button */}
          <div className="relative">
            <StaffQuickStats token={token} />
            <div className="mb-4 -mt-2 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPerformanceDashboard(true)}
                className="flex items-center gap-2"
                data-testid="view-performance-dashboard-btn"
              >
                <TrendingUp className="w-4 h-4" />
                View full performance dashboard
              </Button>
            </div>
          </div>

          {/* Performance Dashboard Modal */}
          {showPerformanceDashboard && (
            <StaffPerformanceDashboard 
              token={token} 
              onClose={() => setShowPerformanceDashboard(false)} 
            />
          )}

          <TabsContent value="my-jobs" className="space-y-4">
            {/* View Toggle */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg text-ink">My jobs <span className="text-slate-400">({myJobs.length})</span></h2>
              <div className="flex items-center gap-1 rounded-full border border-hair bg-white p-1">
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="flex items-center gap-2"
                  data-testid="staff-view-list"
                >
                  <List className="w-4 h-4" />
                  List
                </Button>
                <Button
                  variant={viewMode === "calendar" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("calendar")}
                  className="flex items-center gap-2"
                  data-testid="staff-view-calendar"
                >
                  <CalendarDays className="w-4 h-4" />
                  Calendar
                </Button>
              </div>
            </div>

            {myJobs.length === 0 ? (
              <EmptyState
                icon={Briefcase}
                title="No jobs yet"
                line="New jobs appear here once they're released."
                testId="staff-no-jobs"
              />
            ) : (
              <>
                {viewMode === "list" ? (
                  myJobs
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      onCheckIn={handleCheckIn}
                      onContactClient={handleContactClient}
                      token={token}
                      shouldShowClientDetails={shouldShowClientDetails}
                      user={user}
                      onDownloadICS={downloadICSFile}
                      onGoogleCalendar={addToGoogleCalendar}
                      onOutlookCalendar={addToOutlookCalendar}
                    />
                  ))
                ) : (
                  <CalendarView
                    jobs={myJobs}
                    token={token}
                    onCheckIn={handleCheckIn}
                    onContactClient={handleContactClient}
                  />
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="available" className="space-y-4">
            {availableJobs.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="Nothing available right now"
                line="New jobs appear here once they're released."
                testId="staff-no-available"
              />
            ) : (
              availableJobs.map((job) => (
                <Card key={job.id} className="bd-card-accent mb-4 bg-white transition-shadow duration-300 hover:shadow-gold">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base text-ink">
                          {job.job_type}
                        </CardTitle>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {ukDate(job.date)}
                          </Badge>
                          {job.time && (
                            <Badge variant="outline" className="text-xs">
                              {ukTime(job.time) || job.time}
                            </Badge>
                          )}
                          <OrderChip value={job.order_number || job.job_number} fallback="" />
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800 shrink-0">Available</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Client details hidden for privacy - only shown when assigned and within 1 week */}
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <User className="w-4 h-4" />
                      <span className="font-medium text-slate-400 italic">Client details available after assignment</span>
                    </div>
                    
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 mt-0.5" />
                      <span>{job.location}</span>
                    </div>

                    {job.equipment_needed && (
                      <div className="flex items-start gap-2 text-sm text-slate-600">
                        <Briefcase className="w-4 h-4 mt-0.5" />
                        <span>{job.equipment_needed}</span>
                      </div>
                    )}

                    {/* Staff Requirements Information */}
                    <div className="rounded-[10px] border border-hair bg-cream p-3 bd-card-rule">
                      <h4 className="mb-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-ink">
                        <Users className="w-4 h-4 text-gold-deep" />
                        Staff required
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Required Staff:</span>
                          <span className="font-semibold text-slate-800">
                            {job.required_staff_count || 1} {(job.required_staff_count || 1) === 1 ? 'person' : 'people'}
                          </span>
                        </div>
                        
                        {job.assigned_staff_emails && job.assigned_staff_emails.length > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600">Currently Assigned:</span>
                            <span className="font-semibold text-blue-600">
                              {job.assigned_staff_emails.length} / {job.required_staff_count || 1}
                            </span>
                          </div>
                        )}
                        
                        {job.status === 'partially_assigned' && (
                          <div className="text-xs text-blue-600 font-medium mt-1">
                            Still accepting requests - {((job.required_staff_count || 1) - (job.assigned_staff_emails?.length || 0))} more needed
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Financial Information */}
                    <div className="rounded-[10px] border border-hair bg-white p-3 bd-card-rule">
                      <h4 className="mb-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-ink">
                        <PoundSterling className="w-4 h-4 text-gold-deep" />
                        What you'll earn
                      </h4>
                      <div className="space-y-2 text-sm">
                        {/* Wage */}
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Wage:</span>
                          <span className="bd-money font-semibold text-ink">
                            {job.wage ? gbp(job.wage) : 'TBC'}
                          </span>
                        </div>
                        
                        {/* Travel - staff member's own estimate for this open job */}
                        <TravelInfo jobId={job.id} token={token} className="mt-1" />
                        
                        {/* Bonus */}
                        <div className="flex justify-between items-start">
                          <span className="text-slate-600">Bonus:</span>
                          <div className="text-right">
                            <span className="font-semibold text-slate-800">?</span>
                            <div className="text-xs text-slate-500 mt-1">
                              Performance dependent
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => handleRequestJob(job.id)}
                      size="lg"
                      className="w-full"
                      data-testid={`request-job-${job.id}`}
                    >
                      Request this job
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {completedJobs.length === 0 ? (
              <EmptyState
                icon={CheckCircle}
                title="Nothing completed yet"
                line="Finished jobs land here with your pay."
                testId="staff-no-completed"
              />
            ) : (
              completedJobs.map((job) => (
                <Card key={job.id} className="mb-4 bg-white">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base text-slate-700">
                          {job.job_type}
                        </CardTitle>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {ukDate(job.date)}
                          </Badge>
                          {job.time && (
                            <Badge variant="outline" className="text-xs">
                              {ukTime(job.time) || job.time}
                            </Badge>
                          )}
                          <OrderChip value={job.order_number || job.job_number} fallback="" />
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800 shrink-0">Completed</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">{job.location}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="chat" className="p-0 h-[calc(100vh-140px)]">
            <StaffChat 
              user={user} 
              token={token} 
              onBack={() => setActiveTab("my-jobs")}
            />
          </TabsContent>

          <TabsContent value="availability" className="space-y-4">
            <MyAvailability token={token} />
          </TabsContent>

          <TabsContent value="my-pay" className="space-y-4">
            <MyPay token={token} />
          </TabsContent>

          <TabsContent value="profile" className="space-y-4">
            <ProfileTab token={token} />
            <StaffAvailabilityCalendar user={user} token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Profile Tab Component
const ProfileTab = ({ token }) => {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    emergency_contact: '',
    home_address: '',
    home_address_line1: '',
    home_postcode: '',
    can_drive: null,
    services_provided: []
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, axiosConfig);
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        phone: response.data.phone || '',
        emergency_contact: response.data.emergency_contact || '',
        home_address: response.data.home_address || '',
        home_address_line1: response.data.home_address_line1 || '',
        home_postcode: response.data.home_postcode || '',
        can_drive: response.data.can_drive,
        services_provided: response.data.services_provided || []
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    try {
      await axios.put(`${API}/profile`, formData, axiosConfig);
      setMessage('Profile updated successfully!');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      setMessage('Error updating profile. Please try again.');
      console.error("Error updating profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Removed old handleUploadPhoto function - replaced with file upload

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('Image size must be less than 5MB');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result;
          
          await axios.post(`${API}/profile/upload-photo`, {
            image_data: base64Data
          }, axiosConfig);

          setMessage('✅ Profile photo updated successfully!');
          fetchProfile();
        } catch (uploadError) {
          setMessage('❌ Error uploading photo. Please try again.');
          console.error("Error uploading photo:", uploadError);
        } finally {
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        setMessage('❌ Error reading file. Please try again.');
        setLoading(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setMessage('❌ Error processing image. Please try again.');
      console.error("Error processing image:", error);
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <Card className="bg-white">
        <CardContent className="p-8 text-center">
          <div className="w-8 h-8 animate-spin border-2 border-yellow-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading profile...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card className="bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              My Profile
            </CardTitle>
            <Button
              variant={editing ? "secondary" : "outline"}
              size="sm"
              onClick={() => setEditing(!editing)}
            >
              <Edit className="w-4 h-4 mr-2" />
              {editing ? 'Cancel' : 'Edit Profile'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {message && (
            <div className={`p-3 rounded-lg text-sm ${
              message.includes('success') 
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          {/* Profile Photo Section */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              <Avatar className="w-20 h-20">
                {profile.profile_photo && (
                  <AvatarImage 
                    src={profile.profile_photo} 
                    alt={profile.name}
                  />
                )}
                <AvatarFallback className="text-xl">
                  {profile.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900">{profile.name}</h3>
              <p className="text-slate-500">{profile.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {loading ? "Uploading..." : "Change Photo"}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Profile Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Full Name
              </label>
              {editing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter your full name"
                />
              ) : (
                <p className="p-2 bg-slate-50 rounded border">{profile.name || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number
              </label>
              {editing ? (
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Enter your phone number"
                />
              ) : (
                <p className="p-2 bg-slate-50 rounded border">{profile.phone || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Emergency Contact
              </label>
              {editing ? (
                <Input
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                  placeholder="Enter emergency contact number"
                />
              ) : (
                <p className="p-2 bg-slate-50 rounded border">{profile.emergency_contact || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Home Address
              </label>
              {editing ? (
                <Input
                  value={formData.home_address}
                  onChange={(e) => setFormData({...formData, home_address: e.target.value})}
                  placeholder="Enter your home address"
                />
              ) : (
                <p className="p-2 bg-slate-50 rounded border">{profile.home_address || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Home Postcode
                <span className="font-normal text-slate-400"> · used for travel money</span>
              </label>
              {editing ? (
                <Input
                  value={formData.home_postcode}
                  onChange={(e) => setFormData({...formData, home_postcode: e.target.value})}
                  placeholder="e.g. LS1 4AP"
                  data-testid="profile-home-postcode-input"
                />
              ) : (
                <p className="p-2 bg-slate-50 rounded border" data-testid="profile-home-postcode">
                  {profile.home_postcode || 'Not set - add it to see travel money'}
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">Only you and the office can see this.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                First Line Of Address <span className="font-normal text-slate-400">(optional)</span>
              </label>
              {editing ? (
                <Input
                  value={formData.home_address_line1}
                  onChange={(e) => setFormData({...formData, home_address_line1: e.target.value})}
                  placeholder="e.g. 12 High Street"
                  data-testid="profile-home-address-line1-input"
                />
              ) : (
                <p className="p-2 bg-slate-50 rounded border">{profile.home_address_line1 || 'Not set'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Driving Status
              </label>
              {editing ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.can_drive === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormData({...formData, can_drive: true})}
                    className="flex-1"
                  >
                    Can Drive
                  </Button>
                  <Button
                    type="button"
                    variant={formData.can_drive === false ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormData({...formData, can_drive: false})}
                    className="flex-1"
                  >
                    Cannot Drive
                  </Button>
                </div>
              ) : (
                <p className="p-2 bg-slate-50 rounded border">
                  {profile.can_drive === true ? 'Can drive' : 
                   profile.can_drive === false ? '🚫 Cannot Drive' : 
                   'Not specified'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Services Provided
              </label>
              {editing ? (
                <div className="grid grid-cols-1 gap-2">
                  {['butler in the buff', 'Life Model', 'Cocktail Tutor', 'Stripper'].map((service) => (
                    <label key={service} className="flex items-center space-x-3 p-2 border border-slate-200 rounded-md hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={(formData.services_provided || []).some((s) => s.toLowerCase() === service.toLowerCase())}
                        onChange={(e) => {
                          const services = formData.services_provided || [];
                          if (e.target.checked) {
                            setFormData({...formData, services_provided: [...services, service]});
                          } else {
                            setFormData({...formData, services_provided: services.filter(s => s.toLowerCase() !== service.toLowerCase())});
                          }
                        }}
                        className="w-4 h-4 text-yellow-600 border-slate-300 rounded focus:ring-yellow-500"
                      />
                      <span className="text-sm text-slate-700">{service}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="p-2 bg-slate-50 rounded border">
                  {profile.services_provided && profile.services_provided.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.services_provided.map((service, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-slate-500">No services specified</span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Account Status
              </label>
              <Badge variant={profile.active ? "default" : "secondary"} className="mt-2">
                {profile.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>

          {editing && (
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Information */}
      <CredentialsCard
        token={token}
        currentEmail={profile.email}
        onEmailChanged={(newToken, newEmail) => {
          localStorage.setItem('token', newToken);
          window.location.reload();
        }}
      />

      <MyRatingCard token={token} />

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Email:</span>
              <span className="font-medium">{profile.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Role:</span>
              <span className="font-medium capitalize">{profile.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Member Since:</span>
              <span className="font-medium">
                {ukDate(profile.created_at)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Account ID:</span>
              <span className="font-mono text-xs">{profile.id.slice(0, 8)}...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Calendar View Component
const AdminCalendarView = ({ jobs }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, week, day

  // Get calendar data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  // Get first day of month and days in month
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

  // Create array of calendar days
  const calendarDays = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Get jobs for a specific day
  const getJobsForDay = (day) => {
    if (!day) return [];
    const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return jobs.filter(job => job.date === dayStr);
  };

  // Get job color based on status
  const getJobColor = (job) => {
    switch (job.status) {
      case 'completed':
        return { bg: '#DCEADF', border: '#2E7D4F', text: '#1F5436' }; // Completed
      case 'assigned':
        return { bg: '#F8F1E1', border: '#E0C178', text: '#121212' }; // Assigned
      case 'available':
        return { bg: '#F7ECD2', border: '#8A6A1E', text: '#614A13' }; // Available
      case 'partially_assigned':
        return { bg: '#FDF6E6', border: '#C9A55C', text: '#7A5C14' }; // Partly filled
      default:
        return { bg: '#FAF7F0', border: '#D8CEB8', text: '#4B4B4B' }; // Other
    }
  };

  const getJobTypeColor = (jobType) => {
    switch(jobType) {
      case 'Cocktail Lesson':
      case 'The Artistic Package':
      case 'Late Night Package':
        return 'bg-cream text-gold-deep border border-hair'
      case 'Butler Service':
        return 'bg-ink text-gold'
      case 'The Ultimate Package':
        return 'bg-gold text-ink'
      case 'Life Modeling':
      case 'Stripper':
        return 'bg-cream text-ink border border-hair'
      default:
        return 'bg-cream text-slate-700 border border-hair'
    }
  }
  // Helper function to format staff assignments
  const formatStaffAssignment = (job) => {
    if (job.assigned_staff_details && job.assigned_staff_details.length > 0) {
      // Multi-staff format
      if (job.assigned_staff_details.length === 1) {
        return job.assigned_staff_details[0].name;
      } else {
        return `${job.assigned_staff_details[0].name} +${job.assigned_staff_details.length - 1}`;
      }
    } else if (job.assigned_staff_name) {
      // Single staff format (backwards compatibility)
      return job.assigned_staff_name;
    }
    return null;
  };

  // Navigation functions
  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Get summary stats for current month
  const monthJobs = jobs.filter(job => {
    const jobDate = new Date(job.date);
    return jobDate.getFullYear() === year && jobDate.getMonth() === month;
  });

  const monthStats = {
    total: monthJobs.length,
    assigned: monthJobs.filter(job => job.status === 'assigned').length,
    available: monthJobs.filter(job => job.status === 'available' || job.status === 'partially_assigned').length,
    completed: monthJobs.filter(job => job.status === 'completed').length
  };

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <Card className="bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Admin Job Calendar
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Complete overview of all jobs - assigned, available, and completed
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Legend */}
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-200 border border-blue-500 rounded"></div>
                  <span>Assigned</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-orange-200 border border-orange-500 rounded"></div>
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-200 border border-green-500 rounded"></div>
                  <span>Completed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation and Stats */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center min-w-[180px]">
                <h3 className="font-bold text-xl text-slate-900">
                  {monthNames[month]} {year}
                </h3>
              </div>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
            </div>

            {/* Month Summary */}
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="font-bold text-lg text-slate-900">{monthStats.total}</div>
                <div className="text-xs text-slate-500">Total Jobs</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-blue-600">{monthStats.assigned}</div>
                <div className="text-xs text-slate-500">Assigned</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-orange-600">{monthStats.available}</div>
                <div className="text-xs text-slate-500">Available</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-green-600">{monthStats.completed}</div>
                <div className="text-xs text-slate-500">Completed</div>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {dayNames.map(day => (
              <div key={day} className="p-3 text-center text-sm font-semibold text-slate-700 bg-slate-100 rounded-lg">
                {day}
              </div>
            ))}
            
            {/* Calendar Days */}
            {calendarDays.map((day, index) => {
              const dayJobs = getJobsForDay(day);
              const isToday = day && new Date().toDateString() === new Date(year, month, day).toDateString();
              
              return (
                <div
                  key={index}
                  className={`min-h-[120px] p-2 border border-slate-200 rounded-lg ${
                    day ? 'bg-white hover:bg-slate-50' : 'bg-slate-50'
                  } ${isToday ? 'ring-2 ring-yellow-400 bg-yellow-50' : ''}`}
                >
                  {day && (
                    <>
                      {/* Day Number */}
                      <div className={`text-sm font-bold mb-2 ${
                        isToday ? 'text-yellow-700' : 'text-slate-700'
                      }`}>
                        {day}
                      </div>
                      
                      {/* Jobs for this day */}
                      <div className="space-y-1">
                        {dayJobs.slice(0, 4).map((job, jobIndex) => {
                          const colors = getJobColor(job);
                          return (
                            <div
                              key={jobIndex}
                              onClick={() => setSelectedJob(job)}
                              className="text-xs p-2 rounded-md cursor-pointer hover:opacity-80 transition-all hover:scale-105"
                              style={{
                                backgroundColor: colors.bg,
                                borderLeft: `3px solid ${colors.border}`,
                                color: colors.text
                              }}
                            >
                              <div className="font-semibold truncate">
                                {job.time}
                              </div>
                              <div className="truncate">
                                {job.job_type}
                              </div>
                              <div className="truncate text-xs opacity-75">
                                {job.client_name}
                              </div>
                              {formatStaffAssignment(job) && (
                                <div className="truncate text-xs font-medium mt-1">
                                  → {formatStaffAssignment(job)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {dayJobs.length > 4 && (
                          <div className="text-xs text-slate-500 text-center bg-slate-100 rounded p-1">
                            +{dayJobs.length - 4} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Job Details Modal */}
      {selectedJob && (
        <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getJobColor(selectedJob).border }}
                ></div>
                {selectedJob.job_type}
                <Badge 
                  style={{ 
                    backgroundColor: getJobColor(selectedJob).bg,
                    color: getJobColor(selectedJob).text,
                    border: `1px solid ${getJobColor(selectedJob).border}`
                  }}
                >
                  {selectedJob.status}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Date & Time:</span>
                  <p className="text-slate-900">{ukDate(selectedJob.date)}{selectedJob.time ? ` · ${ukTime(selectedJob.time) || selectedJob.time}` : ''}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Status:</span>
                  <p className="text-slate-900 capitalize">{selectedJob.status}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Client:</span>
                  <p className="text-slate-900">{selectedJob.client_name}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Phone:</span>
                  <p className="text-slate-900">{selectedJob.client_phone}</p>
                </div>
              </div>

              <div>
                <span className="font-medium text-slate-700">Location:</span>
                <p className="text-slate-900 mt-1">{selectedJob.location}</p>
              </div>

              {(selectedJob.assigned_staff_details?.length > 0 || selectedJob.assigned_staff_name) && (
                <div>
                  <span className="font-medium text-slate-700">
                    {selectedJob.assigned_staff_details?.length > 1 ? 'Team Members:' : 'Assigned Staff:'}
                  </span>
                  <div className="mt-2 space-y-2">
                    {selectedJob.assigned_staff_details?.length > 0 ? (
                      selectedJob.assigned_staff_details.map((staff, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-800">
                            {staff.name}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {staff.email}
                            {staff.phone && ` • ${staff.phone}`}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800">
                          {selectedJob.assigned_staff_name}
                        </Badge>
                        <span className="text-xs text-slate-500">({selectedJob.assigned_staff_email})</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedJob.equipment_needed && (
                <div>
                  <span className="font-medium text-slate-700">Equipment:</span>
                  <p className="text-slate-900 mt-1">{selectedJob.equipment_needed}</p>
                </div>
              )}

              {selectedJob.cocktails_selected && selectedJob.cocktails_selected.length > 0 && (
                <div>
                  <span className="font-medium text-slate-700">Cocktails Selected:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedJob.cocktails_selected.map((cocktail, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {cocktail}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedJob.check_in_confirmed && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800">Staff checked in</span>
                  {selectedJob.check_in_time && (
                    <span className="text-xs text-green-600 ml-auto">
                      {new Date(selectedJob.check_in_time).toLocaleString()}
                    </span>
                  )}
                </div>
              )}

              {selectedJob.client_contacted && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800">Client contacted</span>
                  {selectedJob.client_contact_time && (
                    <span className="text-xs text-blue-600 ml-auto">
                      {new Date(selectedJob.client_contact_time).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Images Tab Component (Admin)
const ImagesTab = ({ token }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageStats, setImageStats] = useState(null);

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchAllImages = async () => {
    try {
      // For demo, we'll simulate images by getting job data
      const jobsResponse = await axios.get(`${API}/admin/jobs`, axiosConfig);
      const jobsWithImages = jobsResponse.data.filter(job => job.image_count > 0);
      
      // Create mock image data based on jobs with images
      const mockImages = jobsWithImages.flatMap(job => 
        Array.from({ length: job.image_count || 1 }, (_, index) => ({
          id: `${job.id}-image-${index}`,
          job_id: job.id,
          staff_email: job.assigned_staff_email,
          staff_name: job.assigned_staff_name,
          image_url: `https://images.unsplash.com/photo-${1556742049 + index}-${Math.random().toString(36).substr(2, 9)}?w=400&h=300&fit=crop`,
          description: `Photo from ${job.job_type} with ${job.client_name}`,
          uploaded_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          bonus_eligible: true,
          bonus_paid: false
        }))
      );
      
      setImages(mockImages);
      
      // Mock stats
      setImageStats({
        total_images: mockImages.length,
        bonus_eligible: mockImages.filter(img => !img.bonus_paid).length,
        bonus_paid: mockImages.filter(img => img.bonus_paid).length
      });
      
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllImages();
  }, []);

  const markBonusPaid = async (imageId) => {
    // Mock bonus marking
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, bonus_paid: true } : img
    ));
  };

  const downloadImage = (imageUrl, description) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `job-photo-${description || 'image'}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Card className="bg-white">
        <CardContent className="p-8 text-center">
          <div className="w-8 h-8 animate-spin border-2 border-yellow-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading images...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {imageStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Image className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{imageStats.total_images}</p>
                  <p className="text-sm text-slate-600">Total Images</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{imageStats.bonus_eligible}</p>
                  <p className="text-sm text-slate-600">Bonus Eligible</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{imageStats.bonus_paid}</p>
                  <p className="text-sm text-slate-600">Bonus Paid</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg text-ink">Job Photos & Bonus Tracking</h2>
          <p className="text-sm text-slate-500">Manage uploaded photos and track staff bonuses</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAllImages}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-8 text-center">
            <Camera className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Images Found</h3>
            <p className="text-slate-500">Staff haven't uploaded any job photos yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {images.map((image) => (
            <Card key={image.id} className="bg-white overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative group">
                <img
                  src={image.image_url}
                  alt={image.description || 'Job photo'}
                  className="w-full h-48 object-cover cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSelectedImage(image)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => downloadImage(image.image_url, image.description)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Bonus Status Badge */}
                <div className="absolute top-2 right-2">
                  {image.bonus_paid ? (
                    <Badge className="bg-green-500 text-white text-xs">
                      Paid
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-500 text-white text-xs">
                      Bonus due
                    </Badge>
                  )}
                </div>
              </div>
              
              <CardContent className="p-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      Job #{image.job_id.slice(0, 8)}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {ukDate(image.uploaded_at)}
                    </span>
                  </div>
                  
                  {image.description && (
                    <p className="text-sm text-slate-600 truncate" title={image.description}>
                      {image.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      By: {image.staff_name}
                    </p>
                    {!image.bonus_paid && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => markBonusPaid(image.id)}
                        className="text-xs h-6"
                      >
                        Mark Paid
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Image Detail Modal */}
      {selectedImage && (
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Job Photo Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.description || 'Job photo'}
                className="w-full max-h-96 object-contain rounded-lg border"
              />
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Job ID:</span>
                  <p className="text-slate-600">#{selectedImage.job_id.slice(0, 8)}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Staff Member:</span>
                  <p className="text-slate-600">{selectedImage.staff_name}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Upload Date:</span>
                  <p className="text-slate-600">
                    {new Date(selectedImage.uploaded_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Bonus Status:</span>
                  <Badge className={selectedImage.bonus_paid ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                    {selectedImage.bonus_paid ? "Paid" : "Pending"}
                  </Badge>
                </div>
              </div>

              {selectedImage.description && (
                <div>
                  <span className="font-medium text-slate-700">Description:</span>
                  <p className="text-slate-600 mt-1">{selectedImage.description}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => downloadImage(selectedImage.image_url, selectedImage.description)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                {!selectedImage.bonus_paid && (
                  <Button
                    onClick={() => {
                      markBonusPaid(selectedImage.id);
                      setSelectedImage({ ...selectedImage, bonus_paid: true });
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Mark Bonus Paid
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Profit Analysis Tab Component
const ProfitAnalysisTab = ({ token }) => {
  const [profitData, setProfitData] = useState(null);
  const [popularServices, setPopularServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().slice(0, 7) + '-01', // Start of current month
    end_date: new Date().toISOString().slice(0, 10), // Today
    period: 'month'
  });

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchProfitAnalysis = async () => {
    setLoading(true);
    try {
      const [profitRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/profit-analysis`, {
          ...axiosConfig,
          params: dateRange
        }),
        axios.get(`${API}/analytics/popular-services`, {
          ...axiosConfig,
          params: dateRange
        })
      ]);
      
      setProfitData(profitRes.data);
      setPopularServices(servicesRes.data);
    } catch (error) {
      console.error("Error fetching profit analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitAnalysis();
  }, [token]);

  // Export functions
  const exportToPDF = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export-pdf`, {
        ...axiosConfig,
        params: dateRange,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `butler-direct-analytics-${dateRange.start_date}-to-${dateRange.end_date}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Failed to export PDF. Please try again.");
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export-csv`, {
        ...axiosConfig,
        params: dateRange,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `butler-direct-analytics-${dateRange.start_date}-to-${dateRange.end_date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  const processAutoCompletions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/process-auto-completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.completed_jobs && result.completed_jobs.length > 0) {
          const jobsList = result.completed_jobs.map(job => 
            `• ${job.job_number}: ${job.staff_name} (${job.duration_hours}h)`
          ).join('\n');
          
          alert(`✅ ${result.message}\n\nCompleted Jobs:\n${jobsList}\n\nAdmin notifications have been sent.`);
        } else {
          alert(`✅ ${result.message}\n\nNo jobs were due for auto-completion at this time.`);
        }
      } else {
        const error = await response.json();
        alert(`❌ Failed to process auto-completions: ${error.detail}`);
      }
    } catch (error) {
      console.error('Auto-completion error:', error);
      alert('❌ Auto-completion failed. Please try again.');
    }
  };

  const getQuickDateRange = (type) => {
    const now = new Date();
    let start, end;

    switch (type) {
      case 'thisWeek':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        start = startOfWeek.toISOString().slice(0, 10);
        end = now.toISOString().slice(0, 10);
        break;
      case 'lastWeek':
        const lastWeekEnd = new Date(now);
        lastWeekEnd.setDate(now.getDate() - now.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        start = lastWeekStart.toISOString().slice(0, 10);
        end = lastWeekEnd.toISOString().slice(0, 10);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
        end = now.toISOString().slice(0, 10);
        break;
      case 'lastMonth':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        start = lastMonth.toISOString().slice(0, 10);
        end = lastMonthEnd.toISOString().slice(0, 10);
        break;
      default:
        return;
    }

    setDateRange({ start_date: start, end_date: end, period: type.includes('Week') ? 'week' : 'month' });
  };

  if (loading && !profitData) {
    return (
      <Card className="p-8 text-center bg-white">
        <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h3 className="text-lg font-medium text-slate-900 mb-2">Loading Profit Analysis</h3>
        <p className="text-slate-500">Calculating job profitability...</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg text-ink">Profit Analysis & Business Analytics</h2>
          <p className="text-sm text-slate-600">Revenue reports, profit analysis, and popular services</p>
        </div>
        <div className="flex gap-3 mb-6">
          <Button
            onClick={exportToCSV}
            variant="outline"
            disabled={popularServices.length === 0}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          
          <Button
            onClick={exportToPDF}
            variant="outline"
            disabled={popularServices.length === 0}
            className="flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </Button>
          
          <Button
            onClick={processAutoCompletions}
            variant="outline"
            className="flex items-center gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
          >
            <CheckCircle className="w-4 h-4" />
            Process Auto-Completions
          </Button>
          
          <Button onClick={fetchProfitAnalysis} disabled={loading}>
            {loading ? "Loading..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      {/* Date Range Controls */}
      <Card className="p-4 bg-white">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Date Range</h3>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => getQuickDateRange('thisWeek')}
            >
              This Week
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => getQuickDateRange('lastWeek')}
            >
              Last Week
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => getQuickDateRange('thisMonth')}
            >
              This Month
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => getQuickDateRange('lastMonth')}
            >
              Last Month
            </Button>
          </div>

          <div className="flex flex-wrap gap-4">
            <UKDatePicker
              label="Start date"
              value={dateRange.start_date}
              onChange={(value) => setDateRange({...dateRange, start_date: value})}
              testId="profit-analysis-start"
            />
            <UKDatePicker
              label="End date"
              value={dateRange.end_date}
              onChange={(value) => setDateRange({...dateRange, end_date: value})}
              testId="profit-analysis-end"
            />
          </div>

          <Button onClick={fetchProfitAnalysis} disabled={loading}>
            Update Analysis
          </Button>
        </div>
      </Card>

      {/* Profit Summary */}
      {profitData && (
        <>
          <Card className="bd-card-accent p-6 bg-cream">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Profit Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">£{profitData.summary.total_profit}</p>
                <p className="text-sm text-slate-600">Total Profit</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{profitData.summary.profit_percentage}%</p>
                <p className="text-sm text-slate-600">Profit Margin</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-700">£{profitData.summary.total_value}</p>
                <p className="text-sm text-slate-600">Total Revenue</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">£{profitData.summary.total_costs}</p>
                <p className="text-sm text-slate-600">Total Costs</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-slate-700">{profitData.summary.total_jobs}</p>
                  <p className="text-sm text-slate-600">Total Jobs</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-700">£{profitData.summary.average_profit_per_job}</p>
                  <p className="text-sm text-slate-600">Avg Profit/Job</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Booked vs Delivered Revenue Analysis */}
          <Card className="p-4 bg-white">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Booked vs Delivered Revenue Analysis</h3>
            
            {popularServices.length === 0 ? (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500">No data available for the selected period</p>
              </div>
            ) : (
              <div className="space-y-4">
                {popularServices.map((service, index) => (
                  <div key={service.service_name} className="bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-slate-900">{service.service_name}</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {service.completion_rate} Complete
                        </span>
                        <span className="text-sm text-slate-500">#{index + 1} Most Booked</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-3">
                      <div className="bg-white rounded p-3">
                        <p className="text-slate-600">Jobs Booked</p>
                        <p className="font-medium text-lg">{service.booked_jobs}</p>
                      </div>
                      <div className="bg-white rounded p-3">
                        <p className="text-slate-600">Jobs Delivered</p>
                        <p className="font-medium text-lg">{service.delivered_jobs}</p>
                      </div>
                      <div className="bg-white rounded p-3">
                        <p className="text-slate-600">Pipeline Value</p>
                        <p className="font-medium text-lg">£{service.pipeline_value}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-green-600 font-medium">Booked Revenue</p>
                        <p className="text-lg">£{service.booked_revenue}</p>
                        <p className="text-xs text-slate-500">Margin: {service.booked_profit_margin}</p>
                      </div>
                      <div>
                        <p className="text-blue-600 font-medium">Delivered Revenue</p>
                        <p className="text-lg">£{service.delivered_revenue}</p>
                        <p className="text-xs text-slate-500">Margin: {service.delivered_profit_margin}</p>
                      </div>
                    </div>
                    
                    {/* Completion rate progress bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-slate-600 mb-1">
                        <span>Completion Rate</span>
                        <span>{service.completion_rate}</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: service.completion_rate.replace('%', '') + '%' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Job Details Table */}
          <Card className="p-4 bg-white">
            <h3 className="bd-heading mb-4 text-base text-ink">Job breakdown</h3>
            <div className="max-h-[70vh] overflow-auto rounded-[10px] border border-hair">
              <table className="bd-table text-sm">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th className="bd-num">Value</th>
                    <th className="bd-num">Staff costs</th>
                    <th className="bd-num">Ingredients</th>
                    <th className="bd-num">Total costs</th>
                    <th className="bd-num">Profit</th>
                    <th className="bd-num">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {profitData.jobs.map((job) => (
                    <tr key={job.job_id}>
                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-ink">{job.job_type}</p>
                          <OrderChip value={job.order_number || job.job_number} fallback="" />
                        </div>
                      </td>
                      <td>{job.client_name}</td>
                      <td className="whitespace-nowrap">{ukDate(job.date)}</td>
                      <td className="bd-num">{gbp(job.job_value)}</td>
                      <td className="bd-num">{gbp(job.wage + job.travel_allowance + job.bonus)}</td>
                      <td className="bd-num">
                        {job.ingredients_cost > 0 ? gbp(job.ingredients_cost) : '—'}
                      </td>
                      <td className="bd-num">{gbp(job.total_costs)}</td>
                      <td className={`bd-num font-bold ${job.profit_gbp >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {gbp(job.profit_gbp)}
                      </td>
                      <td className={`bd-num font-bold ${job.profit_percentage >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {job.profit_percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

// Staff Map Tab Component
const StaffMapTab = ({ token }) => {
  const [staffLocations, setStaffLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedServices, setSelectedServices] = useState([]);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [jobLocation, setJobLocation] = useState('');
  const [distances, setDistances] = useState([]);
  const [calculatingDistances, setCalculatingDistances] = useState(false);
  const [showDistances, setShowDistances] = useState(false);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const mapRef = useRef(null);

  const services = ['butler in the buff', 'Life Model', 'Cocktail Tutor', 'Stripper'];

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchStaffLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/staff-locations`, axiosConfig);
      setStaffLocations(response.data);
    } catch (error) {
      console.error("Error fetching staff locations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffLocations();
  }, [token]);

  useEffect(() => {
    const initMap = async () => {
      if (!window.google && process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        // Load Google Maps API
        const { Loader } = await import('@googlemaps/js-api-loader');
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          version: 'weekly',
          libraries: ['places', 'geometry']
        });

        try {
          await loader.load();
        } catch (error) {
          console.error('Error loading Google Maps:', error);
          return;
        }
      }

      if (window.google && mapRef.current) {
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: { lat: 54.5, lng: -3.0 }, // Center of UK
          zoom: 6,
          styles: [
            {
              featureType: "poi",
              stylers: [{ visibility: "off" }]
            }
          ]
        });
        setMap(newMap);

        // Initialize directions service
        const newDirectionsService = new window.google.maps.DirectionsService();
        const newDirectionsRenderer = new window.google.maps.DirectionsRenderer({
          suppressMarkers: true, // We'll use our custom markers
          polylineOptions: {
            strokeColor: '#3B82F6',
            strokeWeight: 4,
            strokeOpacity: 0.7
          }
        });
        newDirectionsRenderer.setMap(newMap);
        
        setDirectionsService(newDirectionsService);
        setDirectionsRenderer(newDirectionsRenderer);
      }
    };

    initMap();
  }, []);

  useEffect(() => {
    if (map && staffLocations.length > 0) {
      updateMapMarkers();
    }
  }, [map, staffLocations, selectedServices]);

  const updateMapMarkers = async () => {
    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);

    const geocoder = new window.google.maps.Geocoder();
    const newMarkers = [];

    for (const staff of staffLocations) {
      // Filter by services if any are selected
      if (selectedServices.length > 0) {
        const hasSelectedService = staff.services_provided?.some(service =>
          selectedServices.some(sel => sel.toLowerCase() === service.toLowerCase())
        );
        if (!hasSelectedService) continue;
      }

      try {
        const result = await geocodeAddress(geocoder, staff.address);
        if (result) {
          const marker = new window.google.maps.Marker({
            position: result,
            map: map,
            title: staff.name,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#3B82F6"/>
                  <circle cx="12" cy="9" r="2.5" fill="white"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(24, 24)
            }
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: createInfoWindowContent(staff)
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });

          newMarkers.push(marker);
        }
      } catch (error) {
        console.error(`Error geocoding address for ${staff.name}:`, error);
      }
    }

    setMarkers(newMarkers);
  };

  const geocodeAddress = (geocoder, address) => {
    return new Promise((resolve) => {
      geocoder.geocode({ address: address + ', UK' }, (results, status) => {
        if (status === 'OK' && results[0]) {
          resolve(results[0].geometry.location);
        } else {
          resolve(null);
        }
      });
    });
  };

  const createInfoWindowContent = (staff) => {
    const servicesBadges = staff.services_provided?.map(service => 
      `<span style="display: inline-block; background-color: #FEF3C7; color: #D97706; padding: 2px 6px; border-radius: 4px; font-size: 11px; margin: 1px;">${service}</span>`
    ).join(' ') || 'No services specified';

    return `
      <div style="padding: 8px; max-width: 250px;">
        <h3 style="margin: 0 0 8px 0; font-weight: bold; color: #1F2937;">${staff.name}</h3>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Email:</strong> ${staff.email}</p>
        ${staff.phone ? `<p style="margin: 4px 0; font-size: 13px;"><strong>Phone:</strong> ${staff.phone}</p>` : ''}
        <p style="margin: 4px 0; font-size: 13px;"><strong>Address:</strong> ${staff.address}</p>
        <p style="margin: 4px 0; font-size: 13px;"><strong>Can Drive:</strong> ${staff.can_drive ? 'Yes' : staff.can_drive === false ? 'No' : 'Not specified'}</p>
        <div style="margin: 8px 0 0 0;">
          <p style="margin: 0 0 4px 0; font-size: 13px; font-weight: bold;">Services:</p>
          <div>${servicesBadges}</div>
        </div>
      </div>
    `;
  };

  const toggleServiceFilter = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const clearFilters = () => {
    setSelectedServices([]);
  };

  const calculateDistances = async () => {
    if (!jobLocation.trim()) {
      alert('Please enter a job location first');
      return;
    }

    setCalculatingDistances(true);
    try {
      const response = await axios.post(`${API}/admin/calculate-distances`, {
        job_location: jobLocation
      }, axiosConfig);
      
      setDistances(response.data.distances);
      setShowDistances(true);
      
      // Add job location marker to map
      if (map && window.google) {
        await addJobLocationMarker(jobLocation);
        
        // Show route to closest staff member
        if (response.data.distances.length > 0) {
          const closestStaff = response.data.distances[0];
          if (closestStaff.distance_miles !== null) {
            await showRoute(jobLocation, closestStaff.staff_address);
          }
        }
      }
      
    } catch (error) {
      console.error('Error calculating distances:', error);
      alert('Failed to calculate distances. Please try again.');
    } finally {
      setCalculatingDistances(false);
    }
  };

  const addJobLocationMarker = async (location) => {
    const geocoder = new window.google.maps.Geocoder();
    
    try {
      const result = await geocodeAddress(geocoder, location);
      if (result) {
        new window.google.maps.Marker({
          position: result,
          map: map,
          title: 'Job Location',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EF4444"/>
                <circle cx="12" cy="9" r="2.5" fill="white"/>
                <text x="12" y="14" text-anchor="middle" fill="white" font-size="8" font-weight="bold">JOB</text>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });

        // Center map on job location
        map.setCenter(result);
        map.setZoom(10);
      }
    } catch (error) {
      console.error('Error adding job location marker:', error);
    }
  };

  const showRoute = async (origin, destination) => {
    if (!directionsService || !directionsRenderer) return;

    try {
      const result = await new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin: origin + ', UK',
            destination: destination + ', UK',
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL
          },
          (result, status) => {
            if (status === 'OK') {
              resolve(result);
            } else {
              reject(new Error('Directions request failed: ' + status));
            }
          }
        );
      });

      directionsRenderer.setDirections(result);
    } catch (error) {
      console.error('Error showing route:', error);
    }
  };

  const showRouteToStaff = async (staffAddress) => {
    if (!jobLocation.trim()) {
      alert('Please enter a job location first');
      return;
    }
    
    await showRoute(jobLocation, staffAddress);
  };

  const clearDistances = () => {
    setDistances([]);
    setShowDistances(false);
    setJobLocation('');
    
    // Clear directions
    if (directionsRenderer) {
      directionsRenderer.setDirections({routes: []});
    }
    
    // Reset map to show all staff
    updateMapMarkers();
  };

  if (loading) {
    return (
      <Card className="p-8 text-center bg-white">
        <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h3 className="text-lg font-medium text-slate-900 mb-2">Loading Staff Map</h3>
        <p className="text-slate-500">Fetching staff locations...</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg text-ink">Staff Map</h2>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-slate-600">
            {staffLocations.filter(staff => {
              if (selectedServices.length === 0) return true;
              return staff.services_provided?.some(service => selectedServices.some(sel => sel.toLowerCase() === service.toLowerCase()));
            }).length} staff shown
          </Badge>
        </div>
      </div>

      {/* Service Filters */}
      <Card className="p-4 bg-white">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-700 mr-2">Filter by service:</span>
          {services.map(service => (
            <Button
              key={service}
              variant={selectedServices.includes(service) ? "default" : "outline"}
              size="sm"
              onClick={() => toggleServiceFilter(service)}
              className="text-xs"
            >
              {service}
            </Button>
          ))}
          {selectedServices.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-xs text-slate-500"
            >
              Clear All
            </Button>
          )}
        </div>
      </Card>

      {/* Distance Calculator */}
      <Card className="p-4 bg-white">
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900">Distance calculator</h3>
          <p className="text-sm text-slate-600">Enter a job location to find the closest staff and calculate travel distances</p>
          
          <div className="flex gap-2">
            <Input
              placeholder="Enter job address (e.g., 123 High Street, London)"
              value={jobLocation}
              onChange={(e) => setJobLocation(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && calculateDistances()}
            />
            <Button 
              onClick={calculateDistances}
              disabled={calculatingDistances || !jobLocation.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {calculatingDistances ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Calculating...
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 mr-2" />
                  Calculate Distances
                </>
              )}
            </Button>
            {showDistances && (
              <Button 
                onClick={clearDistances}
                variant="outline"
                className="text-slate-600"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Distance Results */}
      {showDistances && distances.length > 0 && (
        <Card className="p-4 bg-white">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Staff distance results</h3>
              <Badge variant="outline" className="bg-green-50 text-green-700">
                {distances.filter(d => d.distance_miles !== null).length} routes calculated
              </Badge>
            </div>
            
            <div className="grid gap-3">
              {distances.slice(0, 10).map((staff, index) => (
                <div 
                  key={staff.staff_id}
                  className={`p-3 border rounded-lg transition-colors ${
                    index === 0 ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">{staff.staff_name}</span>
                        {index === 0 && (
                          <Badge className="bg-green-100 text-green-800 text-xs">Closest</Badge>
                        )}
                        {staff.can_drive && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                            Can drive
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <span>{staff.distance_miles ? `${staff.distance_miles} miles` : 'Route not found'}</span>
                        <span>⏱️ {staff.traffic_duration_text || staff.duration_text}</span>
                        {staff.staff_phone && (
                          <span>{staff.staff_phone}</span>
                        )}
                      </div>
                      
                      {staff.services_provided && staff.services_provided.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {staff.services_provided.map((service, idx) => (
                            <Badge 
                              key={idx} 
                              variant="secondary" 
                              className="text-xs bg-yellow-50 text-yellow-700"
                            >
                              {service}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {staff.distance_miles !== null && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => showRouteToStaff(staff.staff_address)}
                          className="text-xs"
                        >
                          <Navigation className="w-3 h-3 mr-1" />
                          Show Route
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {distances.length > 10 && (
              <p className="text-sm text-slate-500 text-center">
                Showing closest 10 staff members • {distances.length - 10} more available
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Map Container */}
      <Card className="bg-white overflow-hidden">
        <div ref={mapRef} style={{ height: '500px', width: '100%' }}>
          {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
            <div className="flex items-center justify-center h-full bg-slate-50">
              <div className="text-center p-8">
                <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Google Maps Not Available</h3>
                <p className="text-slate-500">Google Maps API key not configured</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Staff Summary */}
      <Card className="p-4 bg-white">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Staff Coverage Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map(service => {
            const count = staffLocations.filter(staff => 
              staff.services_provided?.includes(service)
            ).length;
            return (
              <div key={service} className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{count}</p>
                <p className="text-sm text-slate-600">{service}</p>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

// Distance Display Component for Staff
const JobDistanceDisplay = ({ jobLocation, token }) => {
  const [distance, setDistance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculated, setCalculated] = useState(false);

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const calculateDistance = async () => {
    if (!jobLocation) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/staff/calculate-job-distance`, {
        job_location: jobLocation
      }, axiosConfig);
      setDistance(response.data);
      setCalculated(true);
    } catch (error) {
      console.error("Error calculating distance:", error);
      setDistance({ error: "Failed to calculate distance" });
      setCalculated(true);
    } finally {
      setLoading(false);
    }
  };

  if (!calculated) {
    return (
      <div className="flex items-center gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={calculateDistance}
          disabled={loading}
          className="text-xs"
        >
          {loading ? (
            <>
              <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-1"></div>
              Calculating...
            </>
          ) : (
            <>
              <Navigation className="w-3 h-3 mr-1" />
              Calculate Distance
            </>
          )}
        </Button>
      </div>
    );
  }

  if (distance?.error) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
        <Navigation className="w-3 h-3" />
        <span>{distance.error}</span>
      </div>
    );
  }

  if (distance?.distance_miles) {
    return (
      <div className="flex items-center gap-4 text-xs text-slate-600 mt-2 p-2 bg-blue-50 rounded-md">
        <div className="flex items-center gap-1">
          <Navigation className="w-3 h-3" />
          <span>{distance.distance_miles} miles</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{distance.duration_minutes} min drive</span>
        </div>
      </div>
    );
  }

  return null;
};

// Job Management Card Component
const JobManagementCard = ({ 
  job, 
  progress,
  onEdit, 
  onDelete, 
  onNotify, 
  onComplete, 
  showCompleteButton = false,
  borderColor = "border-l-slate-400",
  statusColor = "bg-slate-100 text-slate-800",
  isCompleted = false,
  token
}) => {
  const [bookingTexts, setBookingTexts] = useState(job.booking_texts_sent || []);
  return (
    <Card className={`bg-white ${borderColor} border-l-[3px] transition-shadow hover:shadow-gold`} data-testid={`admin-job-card-${job.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h3 className="font-bold text-ink">{job.job_type}</h3>
              <OrderChip value={job.order_number || job.job_number} />
              <Badge className={`${statusColor} capitalize`}>
                {String(job.status || '').replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="space-y-1 text-sm text-slate-600">
              <p><strong>Client:</strong> {job.client_name} ({job.client_phone})</p>
              {(job.wage || job.travel_allowance || job.bonus) && (
                <p><strong>Compensation:</strong> 
                  <span className="ml-2">
                    {job.wage && `${gbp(job.wage)} wage`}
                    {job.wage && (job.travel_allowance || job.bonus) && ', '}
                    {job.travel_allowance && `${gbp(job.travel_allowance)} travel`}
                    {job.travel_allowance && job.bonus && ', '}
                    {job.bonus && `${gbp(job.bonus)} bonus`}
                    {(job.wage || job.travel_allowance || job.bonus) && (
                      <span className="bd-money ml-2 font-bold text-ink">
                        (Total: {gbp(parseFloat(job.wage || 0) + parseFloat(job.travel_allowance || 0) + parseFloat(job.bonus || 0))})
                      </span>
                    )}
                  </span>
                </p>
              )}
              <p><strong>Date &amp; time:</strong> {ukDate(job.date)}{job.time ? ` · ${ukTime(job.time) || job.time}` : ''}</p>
              <p><strong>Location:</strong> {job.location}</p>
              {(job.assigned_staff_details?.length > 0 || job.assigned_staff_name) && (
                <div><strong>
                  {job.assigned_staff_details?.length > 1 ? 'Team:' : 'Assigned to:'}
                </strong>
                  <span className="inline-flex flex-wrap gap-1 ml-2">
                    {job.assigned_staff_details?.length > 0 ? (
                      job.assigned_staff_details.map((staff, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center rounded-full bg-cream px-2 py-1 text-xs font-semibold text-ink"
                          title={`${staff.email}${staff.phone ? ` • ${staff.phone}` : ''}`}
                        >
                          {staff.name}
                        </span>
                      ))
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-cream px-2 py-1 text-xs font-semibold text-ink">
                        {job.assigned_staff_name}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {(!job.assigned_staff_details || job.assigned_staff_details.length === 0) && !job.assigned_staff_name && job.status === 'available' && (
                <p><strong>Status:</strong> 
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 ml-2">
                    Available for assignment
                  </span>
                </p>
              )}
              {job.equipment_needed && (
                <p><strong>Equipment:</strong> {job.equipment_needed}</p>
              )}
              {job.cocktails_selected && job.cocktails_selected.length > 0 && (
                <div className="mt-2">
                  <p><strong>Cocktails:</strong></p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {job.cocktails_selected.map((cocktail, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {cocktail}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {token && <AdminJobTravel job={job} token={token} />}
            {token && <JobCocktailStatus job={job} token={token} />}
          </div>
          <div className="flex flex-wrap items-center gap-2 md:ml-4">
            <BookingTextButton job={job} token={token} onLogged={setBookingTexts} />
            {job.status === 'available' && onNotify && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNotify(job.id)}
                className="text-xs"
              >
                <Bell className="w-3 h-3 mr-1" />
                Notify staff
              </Button>
            )}
            {showCompleteButton && onComplete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onComplete(job.id)}
                className="text-xs text-green-700"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Mark complete
              </Button>
            )}
            {!isCompleted && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(job)}
              >
                <Edit className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(job.id)}
              className="text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {progress && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 rounded-[10px] border border-hair bg-page p-2"
               data-testid={`job-progress-${job.id}`}>
            {progress.length === 0 ? (
              <span className="text-[11px] text-slate-500">
                Waiting on the team: not messaged, not arrived, not finished
              </span>
            ) : progress.map((row) => (
              <span key={row.staff_email} className="text-[11px] text-slate-500">
                <strong className="text-ink">{(row.staff_name || row.staff_email).split(' ')[0]}</strong>
                {' '}· messaged {row.messaged_at ? ukDate(row.messaged_at) : '—'}
                {' '}· arrived {row.arrived_at ? ukDate(row.arrived_at) : '—'}
                {' '}· all done {row.done_at ? ukDate(row.done_at) : '—'}
              </span>
            ))}
          </div>
        )}

        <BookingTextHistory sent={bookingTexts} jobId={job.id} />

        <JobAvailability job={job} token={token} />

        <JobEmailPanel job={job} token={token} />
      </CardContent>
    </Card>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [jobRequests, setJobRequests] = useState([]);
  const [staffRefreshKey, setStaffRefreshKey] = useState(0);
  const [holdingRefreshKey, setHoldingRefreshKey] = useState(0);
  const [jobProgress, setJobProgress] = useState({});
  const { user, token, logout } = useAuth();

  const releasedJobs = (jobs || []).filter(job => job.release_state !== 'holding');
  const holdingCount = (jobs || []).filter(job => job.release_state === 'holding').length;

  // Finished work is archived, never deleted. Counts shown with Jobs are active only.
  const isArchived = (job) => job.status === 'completed' || job.status === 'cancelled';
  const archivedJobs = releasedJobs.filter(isArchived);
  const activeJobsCount = (jobs || []).filter(job => !isArchived(job)).length;

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true); // Show loading state during refresh
      console.log("Refreshing admin data...");
      
      const [statsRes, staffRes, jobsRes, requestsRes, progressRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, axiosConfig),
        axios.get(`${API}/admin/staff`, axiosConfig),
        axios.get(`${API}/admin/jobs`, axiosConfig),
        axios.get(`${API}/admin/job-requests`, axiosConfig),
        axios.get(`${API}/admin/job-progress`, axiosConfig).catch(() => ({ data: { progress: [] } }))
      ]);

      setStats(statsRes.data);
      setStaff(staffRes.data);
      setJobs(jobsRes.data);
      setJobRequests(requestsRes.data);
      // One call for every job's on-the-day times
      setJobProgress((progressRes.data.progress || []).reduce((acc, row) => {
        (acc[row.job_id] = acc[row.job_id] || []).push(row);
        return acc;
      }, {}));
      setHoldingRefreshKey((k) => k + 1);
      setStaffRefreshKey((k) => k + 1);
      
      console.log("Admin data refreshed successfully");
    } catch (error) {
      console.error("Error fetching admin data:", error);
      alert("Failed to refresh data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateStaff = async (staffData) => {
    try {
      await axios.post(`${API}/admin/staff`, staffData, axiosConfig);
      fetchData();
      setShowStaffModal(false);
    } catch (error) {
      console.error("Error creating staff:", error);
    }
  };

  const handleUpdateStaff = async (staffId, updates) => {
    try {
      console.log("Updating staff:", staffId, "with data:", updates);
      
      // Ensure proper data formatting
      const updateData = { ...updates };
      
      // Remove any undefined values that might cause validation issues
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      // Don't send empty password field
      if (updateData.password === '') {
        delete updateData.password;
      }
      
      console.log("Sending staff update request with:", updateData);
      const response = await axios.put(`${API}/admin/staff/${staffId}`, updateData, axiosConfig);
      console.log("Staff update response:", response.data);
      
      fetchData();
      setEditingStaff(null);
      alert("✅ Staff member updated successfully!");
    } catch (error) {
      console.error("Error updating staff:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Show detailed error message
      let errorMessage = "Failed to update staff member";
      if (error.response?.status === 422) {
        errorMessage += " - Data validation error. Please check all fields are filled correctly.";
        if (error.response?.data?.detail) {
          errorMessage += `\n\nDetails: ${JSON.stringify(error.response.data.detail)}`;
        }
      } else if (error.response?.data?.detail) {
        errorMessage += `: ${error.response.data.detail}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    const confirmDelete = window.confirm(
      "⚠️ Are you sure you want to permanently delete this staff member? This action cannot be undone.\n\n" +
      "Note: Staff members with assigned jobs cannot be deleted."
    );
    
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/admin/staff/${staffId}`, axiosConfig);
      alert('✅ Staff member deleted successfully');
      fetchData();
    } catch (error) {
      console.error("Error deleting staff:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete staff member";
      alert("❌ " + errorMsg);
    }
  };

  const handleDeactivateStaff = async (staffId) => {
    const confirmDeactivate = window.confirm(
      "Are you sure you want to deactivate this staff member?\n\n" +
      "They will not be able to login, but their record will be kept."
    );
    
    if (!confirmDeactivate) return;

    try {
      await axios.put(`${API}/admin/staff/${staffId}/deactivate`, {}, axiosConfig);
      alert('✅ Staff member deactivated successfully');
      fetchData();
    } catch (error) {
      console.error("Error deactivating staff:", error);
      const errorMsg = error.response?.data?.detail || "Failed to deactivate staff member";
      alert("❌ " + errorMsg);
    }
  };

  const handleCreateJob = async (jobData) => {
    try {
      console.log("Creating job with data:", jobData);
      
      // Ensure proper data formatting
      const createData = { ...jobData };
      
      // Convert financial fields from strings to numbers
      if (createData.wage !== '' && createData.wage !== null) {
        createData.wage = parseFloat(createData.wage) || null;
      } else {
        createData.wage = null;
      }
      
      if (createData.travel_allowance !== '' && createData.travel_allowance !== null) {
        createData.travel_allowance = parseFloat(createData.travel_allowance) || null;
      } else {
        createData.travel_allowance = null;
      }
      
      if (createData.bonus !== '' && createData.bonus !== null) {
        createData.bonus = parseFloat(createData.bonus) || null;
      } else {
        createData.bonus = null;
      }
      
      // Handle job_value conversion
      if (createData.job_value !== '' && createData.job_value !== null) {
        createData.job_value = parseFloat(createData.job_value) || null;
      } else {
        createData.job_value = null;
      }
      
      // Handle ingredients_cost conversion
      if (createData.ingredients_cost !== '' && createData.ingredients_cost !== null) {
        createData.ingredients_cost = parseFloat(createData.ingredients_cost) || null;
      } else {
        createData.ingredients_cost = null;
      }
      
      // Ensure date is in proper format (YYYY-MM-DD)
      if (createData.date && !createData.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Convert from DD/MM/YYYY to YYYY-MM-DD if needed
        const dateParts = createData.date.split('/');
        if (dateParts.length === 3) {
          createData.date = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}-${dateParts[0].padStart(2, '0')}`;
        }
      }
      
      // Remove any undefined values that might cause validation issues
      Object.keys(createData).forEach(key => {
        if (createData[key] === undefined) {
          delete createData[key];
        }
      });
      
      console.log("Sending job creation request with:", createData);
      const response = await axios.post(`${API}/admin/jobs`, createData, axiosConfig);
      console.log("Job creation response:", response.data);
      
      fetchData();
      setShowJobModal(false);
      alert("✅ Job created successfully!");
    } catch (error) {
      console.error("Error creating job:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Show detailed error message
      let errorMessage = "Failed to create job";
      if (error.response?.status === 422) {
        errorMessage += " - Data validation error. Please check all required fields are filled correctly.";
        if (error.response?.data?.detail) {
          errorMessage += `\n\nDetails: ${JSON.stringify(error.response.data.detail)}`;
        }
      } else if (error.response?.data?.detail) {
        errorMessage += `: ${error.response.data.detail}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    try {
      console.log("Updating job:", jobId, "with data:", updates);
      
      // Ensure proper data formatting
      const updateData = { ...updates };
      
      // Convert financial fields from strings to numbers
      if (updateData.wage !== undefined) {
        if (updateData.wage !== '' && updateData.wage !== null) {
          updateData.wage = parseFloat(updateData.wage) || null;
        } else {
          updateData.wage = null;
        }
      }
      
      if (updateData.travel_allowance !== undefined) {
        if (updateData.travel_allowance !== '' && updateData.travel_allowance !== null) {
          updateData.travel_allowance = parseFloat(updateData.travel_allowance) || null;
        } else {
          updateData.travel_allowance = null;
        }
      }
      
      if (updateData.bonus !== undefined) {
        if (updateData.bonus !== '' && updateData.bonus !== null) {
          updateData.bonus = parseFloat(updateData.bonus) || null;
        } else {
          updateData.bonus = null;
        }
      }
      
      if (updateData.job_value !== undefined) {
        if (updateData.job_value !== '' && updateData.job_value !== null) {
          updateData.job_value = parseFloat(updateData.job_value) || null;
        } else {
          updateData.job_value = null;
        }
      }
      
      if (updateData.ingredients_cost !== undefined) {
        if (updateData.ingredients_cost !== '' && updateData.ingredients_cost !== null) {
          updateData.ingredients_cost = parseFloat(updateData.ingredients_cost) || null;
        } else {
          updateData.ingredients_cost = null;
        }
      }
      
      // Ensure date is in proper format (YYYY-MM-DD)
      if (updateData.date && !updateData.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Convert from DD/MM/YYYY to YYYY-MM-DD if needed
        const dateParts = updateData.date.split('/');
        if (dateParts.length === 3) {
          updateData.date = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}-${dateParts[0].padStart(2, '0')}`;
        }
      }
      
      // Handle unassignment properly
      if (updateData.assigned_staff_emails && updateData.assigned_staff_emails.length === 0) {
        updateData.assigned_staff_email = null;
        updateData.assigned_staff_emails = [];
        console.log("Unassigning job - clearing all staff assignments");
      }
      
      // Remove any undefined or null values that might cause validation issues
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      console.log("Sending update request with:", updateData);
      const response = await axios.put(`${API}/admin/jobs/${jobId}`, updateData, axiosConfig);
      console.log("Update response:", response.data);
      
      fetchData();
      setEditingJob(null);
      alert("Job updated successfully!");
    } catch (error) {
      console.error("Error updating job:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Show detailed error message
      let errorMessage = "Failed to update job";
      if (error.response?.status === 422) {
        errorMessage += " - Data validation error. Please check all required fields are filled correctly.";
        if (error.response?.data?.detail) {
          errorMessage += `\n\nDetails: ${JSON.stringify(error.response.data.detail)}`;
        }
      } else if (error.response?.data?.detail) {
        errorMessage += `: ${error.response.data.detail}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, axiosConfig);
      fetchData();
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  const handleMarkJobComplete = async (jobId) => {
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/mark-complete`, {}, axiosConfig);
      fetchData(); // Refresh data to update job status
      alert("✅ Job marked as complete successfully!");
    } catch (error) {
      console.error("Error marking job complete:", error);
      alert("❌ Failed to mark job as complete. Please try again.");
    }
  };

  const handleSendNotification = async (notificationData) => {
    try {
      await axios.post(`${API}/admin/notifications/send`, notificationData, axiosConfig);
      setShowNotificationModal(false);
      toast.success('Alert sent to staff');
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error(error.response?.data?.detail || 'Could not send that alert');
    }
  };

  const handleNotifyJobAvailable = async (jobId) => {
    try {
      await axios.post(`${API}/admin/notifications/job-available`, null, {
        ...axiosConfig,
        params: { job_id: jobId }
      });
      toast.success('Job availability notification sent to all staff');
    } catch (error) {
      console.error("Error sending job notification:", error);
      toast.error(error.response?.data?.detail || 'Could not send that notification');
    }
  };
  const handleApproveRequest = async (requestId) => {
    try {
      await axios.post(`${API}/admin/job-requests/${requestId}/approve`, {}, axiosConfig);
      alert('Job request approved! Staff member has been notified.');
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error approving request:", error);
      const errorMsg = error.response?.data?.detail || "Failed to approve request";
      alert("❌ " + errorMsg);
    }
  };

  const handleDenyRequest = async (requestId) => {
    try {
      await axios.post(`${API}/admin/job-requests/${requestId}/deny`, {}, axiosConfig);
      alert('Job request denied. Staff member has been notified.');
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error denying request:", error);
      const errorMsg = error.response?.data?.detail || "Failed to deny request";
      alert("❌ " + errorMsg);
    }
  };

  if (loading) {
    return <BrandLoading line="Opening the office…" testId="admin-loading" />;
  }

  return (
    <div className="min-h-screen bg-page">
      <BrandTopBar subtitle="Office" testId="admin-top-bar">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowNotificationModal(true)}
          className="border-gold/30 bg-transparent text-cream hover:bg-white/10 hover:text-cream"
          data-testid="admin-send-alert-btn"
        >
          <Send className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Send alert</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="border-gold/30 bg-transparent text-cream hover:bg-white/10 hover:text-cream"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 sm:mr-1 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{loading ? 'Refreshing…' : 'Refresh'}</span>
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={logout}
          aria-label="Sign out"
          className="border-gold/30 bg-transparent text-cream hover:bg-white/10 hover:text-cream"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </BrandTopBar>

      {/* Main Content */}
      <main className="mx-auto max-w-[1600px] p-4">
        <div className="mb-4 bd-fade-up">
          <h1 className="text-xl text-ink sm:text-2xl">Welcome, {firstName(user?.name)}</h1>
          <p className="mt-1 text-sm text-slate-500">{ukDateLong(new Date())}</p>
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Desktop Navigation - Hidden on Mobile */}
          <TabsList className="bd-tabs-dark mb-6 hidden w-full flex-wrap justify-start gap-1 rounded-[14px] p-1.5 md:flex">
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="admin-dashboard-tab">
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2" data-testid="admin-calendar-tab">
              <CalendarDays className="w-4 h-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="staff" className="flex items-center gap-2" data-testid="admin-staff-tab">
              <Users className="w-4 h-4" />
              Staff ({staff.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-2" data-testid="admin-jobs-tab">
              <Briefcase className="w-4 h-4" />
              Jobs ({activeJobsCount})
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2" data-testid="admin-requests-tab">
              <Clock className="w-4 h-4" />
              Requests ({jobRequests.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="admin-messages-tab">
              <MessageCircle className="w-4 h-4" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="images" className="flex items-center gap-2" data-testid="admin-images-tab">
              <Image className="w-4 h-4" />
              Images
            </TabsTrigger>
            <TabsTrigger value="profit" className="flex items-center gap-2" data-testid="admin-profit-tab">
              <DollarSign className="w-4 h-4" />
              Profit Analysis
            </TabsTrigger>
            <TabsTrigger value="pay" className="flex items-center gap-2" data-testid="admin-pay-tab">
              <PoundSterling className="w-4 h-4" />
              Pay
            </TabsTrigger>
            <TabsTrigger value="cocktails" className="flex items-center gap-2" data-testid="admin-cocktails-tab">
              <Martini className="w-4 h-4" />
              Cocktails
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="admin-settings-tab">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Mobile Navigation - Visible only on Mobile */}
          <div className="block md:hidden mb-6">
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={() => setActiveTab('dashboard')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'dashboard' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="text-base font-medium">Dashboard</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('jobs')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'jobs' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <Briefcase className="w-5 h-5" />
                <span className="text-base font-medium">Jobs ({activeJobsCount})</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('staff')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'staff' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="text-base font-medium">Staff ({(staff || []).length})</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('requests')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'requests' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <Clock className="w-5 h-5" />
                <span className="text-base font-medium">Requests ({(jobRequests || []).length})</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('calendar')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'calendar' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <CalendarDays className="w-5 h-5" />
                <span className="text-base font-medium">Calendar</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('map')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'map' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <MapPin className="w-5 h-5" />
                <span className="text-base font-medium">Staff Map</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('profit')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'profit' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <DollarSign className="w-5 h-5" />
                <span className="text-base font-medium">Profit</span>
              </button>

              <button 
                onClick={() => setActiveTab('pay')}
                data-testid="admin-pay-mobile-btn"
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'pay' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <PoundSterling className="w-5 h-5" />
                <span className="text-base font-medium">Pay</span>
              </button>

              <button 
                onClick={() => setActiveTab('cocktails')}
                data-testid="admin-cocktails-mobile-btn"
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'cocktails' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <Martini className="w-5 h-5" />
                <span className="text-base font-medium">Cocktails</span>
              </button>

              <button 
                onClick={() => setActiveTab('settings')}
                data-testid="admin-settings-mobile-btn"
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'settings' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <Settings className="w-5 h-5" />
                <span className="text-base font-medium">Settings</span>
              </button>
              
              <button 
                onClick={() => setActiveTab('images')}
                className={`flex items-center gap-2 p-4 h-16 rounded-[12px] border transition-colors ${
                  activeTab === 'images' 
                    ? 'bg-ink border-ink text-cream shadow-card' 
                    : 'bg-white border-hair text-slate-700 hover:border-gold hover:bg-cream'
                }`}
              >
                <Image className="w-5 h-5" />
                <span className="text-base font-medium">Images</span>
              </button>
            </div>
          </div>

          {/* Dashboard Overview */}
          <TabsContent value="dashboard" className="space-y-6">
            <TodayPanel token={token} />
            {stats && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card className="bd-card-accent bd-fade-up bg-white" data-testid="stat-total-staff">
                  <CardContent className="p-5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Total staff</p>
                    <div className="mt-2 text-3xl font-black tabular-nums text-ink">{stats.total_staff}</div>
                    <p className="mt-1 text-xs text-slate-500">{stats.active_staff} active</p>
                  </CardContent>
                </Card>

                <Card className="bd-card-accent bd-fade-up bg-white" style={{ animationDelay: '80ms' }} data-testid="stat-today-jobs">
                  <CardContent className="p-5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Today's jobs</p>
                    <div className="mt-2 text-3xl font-black tabular-nums text-ink">{stats.total_jobs_today}</div>
                    <p className="mt-1 text-xs text-slate-500">{stats.completed_jobs_today} completed</p>
                  </CardContent>
                </Card>

                <Card className="bd-card-accent bd-fade-up bg-white" style={{ animationDelay: '160ms' }} data-testid="stat-pending-jobs">
                  <CardContent className="p-5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Pending jobs</p>
                    <div className="mt-2 text-3xl font-black tabular-nums text-ink">{stats.pending_jobs}</div>
                    <p className="mt-1 text-xs text-slate-500">{stats.available_jobs} available</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Recent Jobs */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-5 w-5 text-gold-deep" />
                  Recent jobs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-500">No jobs yet. New bookings will show here.</p>
                ) : (
                  <div className="space-y-3">
                    {jobs.slice(0, 5).map((job) => (
                      <div key={job.id} className="flex items-center justify-between gap-3 rounded-[10px] border border-hair bg-page p-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate font-semibold text-ink">{job.job_type}</p>
                            <OrderChip value={job.order_number || job.job_number} fallback="" />
                          </div>
                          <p className="truncate text-sm text-slate-500">{job.client_name} • {job.location}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <Badge variant={job.status === 'completed' ? 'default' : 'secondary'} className="capitalize">
                            {String(job.status || '').replace(/_/g, ' ')}
                          </Badge>
                          <p className="mt-1 text-xs text-slate-500">
                            {ukDate(job.date)}{job.time ? ` · ${ukTime(job.time) || job.time}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Management */}
          <TabsContent value="staff" className="space-y-4 md:space-y-4 space-y-6">
            {/* Desktop Header */}
            <div className="hidden md:flex justify-between items-center">
              <h2 className="text-lg text-ink">Staff Management</h2>
              <Button onClick={() => setShowStaffModal(true)} className="bg-yellow-500 hover:bg-yellow-600">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </div>

            {/* Mobile Header */}
            <div className="block md:hidden space-y-4">
              <h2 className="text-xl text-ink text-center">Staff Management</h2>
              <Button 
                onClick={() => setShowStaffModal(true)} 
                className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-lg font-medium"
              >
                <UserPlus className="w-5 h-5 mr-3" />
                Add New Staff Member
              </Button>
            </div>

            <StaffAccountsPanel
              token={token}
              onEdit={setEditingStaff}
              onDelete={handleDeleteStaff}
              refreshKey={staffRefreshKey}
            />
          </TabsContent>

          {/* Jobs Management */}
          <TabsContent value="jobs" className="space-y-4">
            {/* Desktop Header */}
            <div className="hidden md:flex justify-between items-center">
              <h2 className="text-lg text-ink">Jobs Management</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setShowImportModal(true)} 
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="import-bookings-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Import Bookings
                </Button>
                <Button onClick={() => setShowJobModal(true)} className="bg-yellow-500 hover:bg-yellow-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Job
                </Button>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="block md:hidden space-y-4">
              <h2 className="text-xl text-ink text-center">Jobs Management</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setShowImportModal(true)} 
                  className="flex-1 h-14 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <FileSpreadsheet className="w-5 h-5 mr-2" />
                  Import
                </Button>
                <Button 
                  onClick={() => setShowJobModal(true)} 
                  className="flex-1 h-14 bg-yellow-500 hover:bg-yellow-600 text-lg font-medium"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Job
                </Button>
              </div>
            </div>

            {/* Job Templates */}
            <JobTemplateManager 
              token={token} 
              onUseTemplate={(template) => {
                setTemplateData(template);
                setShowJobModal(true);
              }}
            />

            {/* Active work and the archive */}
            <div className="bd-card">
              <Tabs defaultValue="active" className="w-full">
                <div className="border-b border-hair p-4">
                  <TabsList className="h-11 w-full justify-start p-1">
                    <TabsTrigger value="active" className="flex items-center gap-2"
                                 data-testid="jobs-active-tab">
                      <Briefcase className="w-4 h-4" />
                      Active ({activeJobsCount})
                    </TabsTrigger>
                    <TabsTrigger value="archived" className="flex items-center gap-2"
                                 data-testid="jobs-archived-tab">
                      <Archive className="w-4 h-4" />
                      Archived ({archivedJobs.length})
                    </TabsTrigger>
                  </TabsList>
                  <p className="mt-2 text-xs text-slate-500">
                    Active is everything still to do. Completed and cancelled jobs move to
                    Archived, where they still count towards profit, pay and ratings.
                  </p>
                </div>

                <TabsContent value="archived" className="p-4">
                  {!jobs ? (
                    <SkeletonCards count={3} />
                  ) : (
                    <ArchivedJobs
                      jobs={archivedJobs}
                      renderJob={(job) => (
                        <JobManagementCard
                          progress={jobProgress[job.id] || []}
                          key={job.id}
                          job={job}
                          token={token}
                          onEdit={setEditingJob}
                          onDelete={handleDeleteJob}
                          showCompleteButton={false}
                          borderColor={job.status === 'cancelled' ? 'border-l-red-500' : 'border-l-green-600'}
                          statusColor={job.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-800'}
                          isCompleted={job.status === 'completed'}
                        />
                      )}
                    />
                  )}
                </TabsContent>

                <TabsContent value="active" className="p-0">
              <Tabs defaultValue="holding" className="w-full">
                {/* Desktop Status Tabs */}
                <div className="hidden md:block border-b border-slate-200">
                  <TabsList className="h-12 p-1 bg-transparent w-full justify-start">
                    <TabsTrigger
                      value="holding"
                      className="flex items-center gap-2"
                      data-testid="holding-tab"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Holding ({holdingCount})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="to-allocate" 
                      className="flex items-center gap-2"
                      data-testid="to-allocate-tab"
                    >
                      <Circle className="w-4 h-4" />
                      To Allocate ({releasedJobs.filter(job => job.status === 'available' || job.status === 'partially_assigned').length})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="allocated" 
                      className="flex items-center gap-2"
                      data-testid="allocated-tab"
                    >
                      <User className="w-4 h-4" />
                      Allocated ({releasedJobs.filter(job => job.status === 'assigned').length})
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Mobile Status Tabs - Stacked Buttons */}
                <div className="block md:hidden p-4 border-b border-slate-200">
                  <div className="grid grid-cols-1 gap-3">
                    <TabsTrigger
                      value="holding"
                      className="flex min-h-16 items-center justify-between rounded-[12px] border border-hair bg-white p-4"
                      data-testid="holding-tab-mobile"
                    >
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="text-lg font-medium">Holding</span>
                      </div>
                      <div className="text-xl font-bold">{holdingCount}</div>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="to-allocate" 
                      className="flex min-h-16 items-center justify-between rounded-[12px] border border-hair bg-white p-4"
                      data-testid="to-allocate-tab-mobile"
                    >
                      <div className="flex items-center gap-3">
                        <Circle className="w-5 h-5" />
                        <span className="text-lg font-medium">To Allocate</span>
                      </div>
                      <div className="text-xl font-bold">
                        {releasedJobs.filter(job => job.status === 'available' || job.status === 'partially_assigned').length}
                      </div>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="allocated" 
                      className="flex min-h-16 items-center justify-between rounded-[12px] border border-hair bg-white p-4"
                      data-testid="allocated-tab-mobile"
                    >
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5" />
                        <span className="text-lg font-medium">Allocated</span>
                      </div>
                      <div className="text-xl font-bold">
                        {releasedJobs.filter(job => job.status === 'assigned').length}
                      </div>
                    </TabsTrigger>
                  </div>
                </div>

                {/* Holding Tab */}
                <TabsContent value="holding" className="p-4">
                  <HoldingZone
                    token={token}
                    onEdit={setEditingJob}
                    onJobsChanged={fetchData}
                    refreshKey={holdingRefreshKey}
                  />
                </TabsContent>

                {/* To Allocate Tab */}
                <TabsContent value="to-allocate" className="p-4 md:p-4 p-3">
                  {!jobs ? (
                    <SkeletonCards count={3} />
                  ) : releasedJobs.filter(job => job.status === 'available' || job.status === 'partially_assigned').length === 0 ? (
                    <EmptyState icon={Circle} title="Nothing to allocate" line="Every released job has someone on it." testId="admin-no-allocate" />
                  ) : (
                    <div className="grid gap-4 md:gap-4 gap-6">
                      {releasedJobs.filter(job => job.status === 'available' || job.status === 'partially_assigned').map((job) => (
                        <JobManagementCard
                          progress={jobProgress[job.id] || []} 
                          key={job.id} 
                          job={job} 
                          token={token}
                          onEdit={setEditingJob}
                          onDelete={handleDeleteJob}
                          onNotify={handleNotifyJobAvailable}
                          showCompleteButton={false}
                          borderColor="border-l-gold"
                          statusColor="bg-amber-100 text-amber-800"
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Allocated Tab */}
                <TabsContent value="allocated" className="p-4">
                  {!jobs ? (
                    <SkeletonCards count={3} />
                  ) : releasedJobs.filter(job => job.status === 'assigned').length === 0 ? (
                    <EmptyState icon={User} title="Nothing allocated" line="Jobs appear here once staff are assigned." testId="admin-no-allocated" />
                  ) : (
                    <div className="grid gap-4">
                      {releasedJobs.filter(job => job.status === 'assigned').map((job) => (
                        <JobManagementCard
                          progress={jobProgress[job.id] || []} 
                          key={job.id} 
                          job={job} 
                          token={token}
                          onEdit={setEditingJob}
                          onDelete={handleDeleteJob}
                          onComplete={handleMarkJobComplete}
                          showCompleteButton={true}
                          borderColor="border-l-ink"
                          statusColor="bg-cream text-ink"
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Completed jobs now live in the Archived tab */}
              </Tabs>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          {/* Job Requests Management */}
          <TabsContent value="requests" className="space-y-4">
            <div className="bd-card">
              <div className="p-6 border-b border-hair">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg text-ink">Job Requests</h2>
                    <p className="text-sm text-slate-500 mt-1">Review and manage staff job requests</p>
                  </div>
                  <Badge variant="secondary">{jobRequests.length} pending</Badge>
                </div>
              </div>

              <div className="p-6">
                {jobRequests.length === 0 ? (
                  <div className="py-2">
                    <EmptyState icon={Clock} title="No requests waiting" line="Staff requests for released jobs appear here." testId="admin-no-requests" />
                  </div>
                ) : (
                  <JobRequestsPanel
                    token={token}
                    requests={jobRequests}
                    onApprove={handleApproveRequest}
                    onDeny={handleDenyRequest}
                  />
                )}
              </div>
            </div>
          </TabsContent>

          {/* Admin Calendar View */}
          <TabsContent value="calendar" className="space-y-4">
            <AdminCalendarView jobs={jobs} />
          </TabsContent>

          {/* Messages / Chat */}
          <TabsContent value="messages" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Staff Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AdminChatDashboard token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images Management */}
          <TabsContent value="images" className="space-y-4">
            <ImagesTab token={token} />
          </TabsContent>

          {/* Staff Map */}
          <TabsContent value="map" className="space-y-4">
            <StaffMapTab token={token} />
          </TabsContent>

          {/* Profit Analysis */}
          <TabsContent value="profit" className="space-y-4">
            <ProfitAnalysisTab token={token} />
            <ProfitPerJob token={token} staff={staff} />
          </TabsContent>

          <TabsContent value="pay" className="space-y-4">
            <PayRun token={token} />
          </TabsContent>

          <TabsContent value="cocktails" className="space-y-4">
            <CocktailChaseList token={token} />
            <CocktailSubmissions token={token} />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <EmailSettings token={token} />
            <TravelSettings token={token} />
            <ProfitSettings token={token} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Staff Creation/Edit Modal */}
      <StaffModal
        isOpen={showStaffModal || !!editingStaff}
        onClose={() => {
          setShowStaffModal(false);
          setEditingStaff(null);
        }}
        onSave={editingStaff ? 
          (data) => handleUpdateStaff(editingStaff.id, data) : 
          handleCreateStaff
        }
        staff={editingStaff}
      />

      {/* Job Creation/Edit Modal */}
      <JobModal
        isOpen={showJobModal || !!editingJob}
        onClose={() => {
          setShowJobModal(false);
          setEditingJob(null);
          setTemplateData(null);
        }}
        onSave={editingJob ? 
          (data) => handleUpdateJob(editingJob.id, data) : 
          handleCreateJob
        }
        job={editingJob}
        templateData={templateData}
        staff={staff}
      />

      {/* Import Bookings Modal */}
      {showImportModal && (
        <ImportBookings
          token={token}
          onImportComplete={() => {
            fetchData(); // Refresh jobs list
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        onSend={handleSendNotification}
        staff={staff}
      />
    </div>
  );
};

// Staff Modal Component
const StaffModal = ({ isOpen, onClose, onSave, staff }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    emergency_contact: '',
    home_postcode: '',
    home_address_line1: '',
    role: 'staff'
  });

  useEffect(() => {
    if (staff) {
      setFormData({
        name: staff.name || '',
        email: staff.email || '',
        password: '',
        phone: staff.phone || '',
        emergency_contact: staff.emergency_contact || '',
        home_postcode: staff.home_postcode || '',
        home_address_line1: staff.home_address_line1 || '',
        role: staff.role || 'staff'
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        emergency_contact: '',
        home_postcode: '',
        home_address_line1: '',
        role: 'staff'
      });
    }
  }, [staff]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = { ...formData };
    if (staff && !dataToSave.password) {
      delete dataToSave.password; // Don't update password if not provided
    }
    onSave(dataToSave);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{staff ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          <div>
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>
          <div>
            <Input
              type="password"
              placeholder={staff ? "New Password (leave blank to keep current)" : "Password"}
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required={!staff}
            />
          </div>
          <div>
            <Input
              placeholder="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
          </div>
          <div>
            <Input
              placeholder="Emergency Contact"
              value={formData.emergency_contact}
              onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
            />
          </div>
          <div>
            <Input
              placeholder="Home Postcode (for travel money)"
              value={formData.home_postcode}
              onChange={(e) => setFormData({...formData, home_postcode: e.target.value})}
              data-testid="staff-home-postcode-input"
            />
          </div>
          <div>
            <Input
              placeholder="First line of home address (optional)"
              value={formData.home_address_line1}
              onChange={(e) => setFormData({...formData, home_address_line1: e.target.value})}
              data-testid="staff-home-address-line1-input"
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-yellow-500 hover:bg-yellow-600">
              {staff ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Job Modal Component
const JobModal = ({ isOpen, onClose, onSave, job, templateData, staff }) => {
  const [formData, setFormData] = useState({
    job_number: '',
    job_type: '',
    date: '',
    time: '19:00', // Default to 7pm
    location: '',
    client_name: '',
    client_phone: '',
    customer_first_name: '',
    customer_last_name: '',
    client_email: '',
    guest_count: '',
    total_price: '',
    deposit_paid: '',
    balance_due_date: '',
    payment_link: '',
    balance_paid: false,
    cocktails_allowed: '',
    equipment_needed: '',
    cocktails_selected: [],
    wage: '',
    travel_allowance: '',
    bonus: '',
    job_value: '',
    ingredients_cost: '',
    required_staff_count: 1,
    assigned_staff_emails: [],  // Changed to array for multi-staff
    notes: ''
  });

  const [orderNumberInfo, setOrderNumberInfo] = useState(null);
  const [staffCosting, setStaffCosting] = useState(null);

  // Per-person travel money and profit for the staff picker (admin only)
  useEffect(() => {
    if (!isOpen || !job?.id) { setStaffCosting(null); return; }
    const token = localStorage.getItem('token');
    fetch(`${BACKEND_URL}/api/admin/jobs/${job.id}/costing?calculate=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then(setStaffCosting)
      .catch(() => setStaffCosting(null));
  }, [isOpen, job?.id]);

  const checkOrderNumber = async (value) => {
    if (!value) {
      setOrderNumberInfo(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ value });
      if (job?.id) params.set('job_id', job.id);
      const response = await fetch(`${BACKEND_URL}/api/admin/jobs/check-order-number?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setOrderNumberInfo(await response.json());
    } catch (error) {
      setOrderNumberInfo(null);
    }
  };

  const cocktailOptions = [
    'PORNSTAR MARTINI', 'PIÑA COLADA', 'LONG ISLAND ICED TEA', 'ESPRESSO MARTINI', 
    'FRENCH MARTINI', 'MOJITO', 'BLUE LAGOON', 'WOO WOO', 'ELDERFLOWER COOLER', 
    'PURPLE RAIN', 'COSMO DAISY', 'MARGARITA', 'TOM COLLINS', 'DAIQUIRI', 
    'TEQUILA SUNRISE', 'SEX ON THE BEACH'
  ];

  // Generate next job number
  const generateJobNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const jobs = await response.json();
        const jobNumbers = jobs
          .map(job => job.job_number)
          .filter(num => num)
          .map(num => parseInt(String(num).replace(/\D/g, '')))
          .filter(num => !isNaN(num));
        
        const maxNum = jobNumbers.length > 0 ? Math.max(...jobNumbers) : 1360;
        return `BD-${maxNum + 1}`;
      }
    } catch (error) {
      console.error('Error generating job number:', error);
    }
    return `BD-${Date.now().toString().slice(-4)}`; // Fallback
  };

  useEffect(() => {
    if (isOpen) {
      if (job) {
        // Editing existing job
        // Properly handle both old and new assignment formats
        let assignedEmails = [];
        
        if (job.assigned_staff_emails && job.assigned_staff_emails.length > 0) {
          // New multi-staff format
          assignedEmails = job.assigned_staff_emails;
        } else if (job.assigned_staff_email && job.assigned_staff_email !== "unassigned") {
          // Old single-staff format - convert to array
          assignedEmails = [job.assigned_staff_email];
        }
        
        setFormData({
          job_number: job.job_number || '',
          job_type: job.job_type || '',
          date: job.date || '',
          time: job.time || '19:00',
        location: job.location || '',
        client_name: job.client_name || '',
        client_phone: job.client_phone || '',
        customer_first_name: job.customer_first_name || '',
        customer_last_name: job.customer_last_name || '',
        client_email: job.client_email || '',
        guest_count: job.guest_count ?? '',
        total_price: job.total_price ?? '',
        deposit_paid: job.deposit_paid ?? '',
        balance_due_date: job.balance_due_date || job.invoice_due_date || '',
        payment_link: job.payment_link || '',
        balance_paid: !!job.balance_paid,
        cocktails_allowed: job.cocktails_allowed || '',
        equipment_needed: job.equipment_needed || '',
        cocktails_selected: job.cocktails_selected || [],
        wage: job.wage || '',
        travel_allowance: job.travel_allowance || '',
        bonus: job.bonus || '',
        job_value: job.job_value || '',
        ingredients_cost: job.ingredients_cost || '',
        required_staff_count: job.required_staff_count || 1,
        assigned_staff_emails: assignedEmails,
        notes: job.notes || ''
      });
      } else {
        // Creating new job - generate job number
        const initializeNewJob = async () => {
          const newJobNumber = await generateJobNumber();
          const baseFormData = {
            job_number: newJobNumber,
            job_type: '',
            date: '',
            time: '19:00', // Default to 7pm
            location: '',
            client_name: '',
            client_phone: '',
            customer_first_name: '',
            customer_last_name: '',
            client_email: '',
            guest_count: '',
            total_price: '',
            deposit_paid: '',
            balance_due_date: '',
            payment_link: '',
            balance_paid: false,
            cocktails_allowed: '',
            equipment_needed: '',
            cocktails_selected: [],
            wage: '',
            travel_allowance: '',
            bonus: '',
            job_value: '',
            ingredients_cost: '',
            required_staff_count: 1,
            assigned_staff_emails: [],
            notes: ''
          };

          // If template data is provided, merge it with base form data
          if (templateData) {
            setFormData({
              ...baseFormData,
              job_type: templateData.job_type || baseFormData.job_type,
              client_name: templateData.client_name || baseFormData.client_name,
              location: templateData.location || baseFormData.location,
              time: templateData.typical_time || baseFormData.time,
              wage: templateData.typical_wage || baseFormData.wage,
              job_value: templateData.typical_job_value || baseFormData.job_value
            });
          } else {
            setFormData(baseFormData);
          }
        };
        
        initializeNewJob();
      }
    }
  }, [isOpen, job, templateData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("JobModal form submitted with data:", formData);
    
    try {
      // Validate required fields
      if (!formData.job_type || !formData.date || !formData.time || !formData.location || !formData.client_name || !formData.client_phone) {
        alert("Please fill in all required fields");
        return;
      }
      
      console.log("Form validation passed, calling onSave...");
      onSave(formData);
    } catch (error) {
      console.error("Error in JobModal handleSubmit:", error);
      alert("An error occurred while saving the job. Please try again.");
    }
  };

  const toggleCocktail = (cocktail) => {
    const updated = formData.cocktails_selected.includes(cocktail)
      ? formData.cocktails_selected.filter(c => c !== cocktail)
      : [...formData.cocktails_selected, cocktail];
    setFormData({...formData, cocktails_selected: updated});
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{job ? 'Edit Job' : 'Create New Job'}</DialogTitle>
        </DialogHeader>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Order number
            </label>
            <Input
              placeholder="e.g. 1496 or BD-1496"
              value={formData.job_number}
              onChange={(e) => setFormData({...formData, job_number: e.target.value})}
              onBlur={(e) => checkOrderNumber(e.target.value)}
              data-testid="job-order-number-input"
            />
            {orderNumberInfo?.order_number && (
              <p className="text-xs text-slate-500 mt-1" data-testid="job-order-number-preview">
                Saves as <strong>{orderNumberInfo.order_number}</strong>
              </p>
            )}
            {orderNumberInfo?.in_use && (
              <p className="text-xs text-amber-700 mt-1" data-testid="job-order-number-warning">
                Careful: {orderNumberInfo.job?.client_name} on {orderNumberInfo.job?.date} already uses {orderNumberInfo.order_number}
              </p>
            )}
          </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Job Type</label>
              <select 
                value={formData.job_type} 
                onChange={(e) => setFormData({...formData, job_type: e.target.value})}
                className="w-full"
              >
                <option value="">Select Job Type</option>
                <option value="Cocktail Lesson">Cocktail Lesson</option>
                <option value="Butler Service">Butler Service</option>
                <option value="Life Modeling">Life Modeling</option>
                <option value="Stripper">Stripper</option>
                <option value="The Artistic Package">The Artistic Package</option>
                <option value="Late Night Package">Late Night Package</option>
                <option value="The Ultimate Package">The Ultimate Package</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Staff Required
              </label>
              <select 
                value={formData.required_staff_count} 
                onChange={(e) => setFormData({...formData, required_staff_count: parseInt(e.target.value)})}
                className="w-full"
              >
                <option value={1}>1 Person</option>
                <option value={2}>2 People</option>
                <option value={3}>3 People</option>
                <option value={4}>4 People</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Assign Staff Members (Optional)
              </label>
              <StaffSelector
                staff={staff}
                selectedEmails={formData.assigned_staff_emails}
                onChange={(emails) => setFormData({...formData, assigned_staff_emails: emails})}
                costing={staffCosting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    setFormData({...formData, date: today});
                  }}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setFormData({...formData, date: tomorrow.toISOString().split('T')[0]});
                  }}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Tomorrow
                </button>
              </div>
            </div>
            <div>
              <Input
                placeholder="Time (e.g., 2:00 PM)"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                placeholder="Client Name"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                required
              />
            </div>
            <div>
              <Input
                placeholder="Client Phone"
                value={formData.client_phone}
                onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                required
              />
            </div>
          </div>

          {/* Customer and payment details, used by the customer emails */}
          <div className="rounded-[10px] border border-hair bg-cream p-3 space-y-3">
            <p className="bd-heading text-[11px] text-gold-deep">Customer and payment details (all optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Customer first name" value={formData.customer_first_name}
                     onChange={(e) => setFormData({...formData, customer_first_name: e.target.value})}
                     data-testid="job-customer-first-name" />
              <Input placeholder="Customer last name" value={formData.customer_last_name}
                     onChange={(e) => setFormData({...formData, customer_last_name: e.target.value})}
                     data-testid="job-customer-last-name" />
              <Input type="email" placeholder="Customer email" value={formData.client_email}
                     onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                     data-testid="job-customer-email" />
              <Input type="number" min="1" placeholder="Number of guests" value={formData.guest_count}
                     onChange={(e) => setFormData({...formData, guest_count: e.target.value})}
                     data-testid="job-guest-count" />
              <Input type="number" step="0.01" placeholder="Total price (£)" value={formData.total_price}
                     onChange={(e) => setFormData({...formData, total_price: e.target.value})}
                     data-testid="job-total-price" />
              <Input type="number" step="0.01" placeholder="Deposit paid (£)" value={formData.deposit_paid}
                     onChange={(e) => setFormData({...formData, deposit_paid: e.target.value})}
                     data-testid="job-deposit-paid" />
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <UKDatePicker label="Balance due date" value={formData.balance_due_date}
                            onChange={(value) => setFormData({...formData, balance_due_date: value})}
                            testId="job-balance-due-date" />
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-semibold text-slate-500">Payment link</label>
                <Input placeholder="https://…" value={formData.payment_link}
                       onChange={(e) => setFormData({...formData, payment_link: e.target.value})}
                       data-testid="job-payment-link" />
              </div>
              <label className="flex min-h-11 items-center gap-2 text-sm font-semibold text-ink">
                <input type="checkbox" checked={!!formData.balance_paid}
                       onChange={(e) => setFormData({...formData, balance_paid: e.target.checked})}
                       className="h-4 w-4 accent-[#E0C178]"
                       data-testid="job-balance-paid" />
                Balance paid
              </label>
            </div>
            <div className="max-w-[220px]">
              <label className="text-xs font-semibold text-slate-500">Cocktail allowance</label>
              <Input placeholder="e.g. 4, or leave blank for any" value={formData.cocktails_allowed}
                     onChange={(e) => setFormData({...formData, cocktails_allowed: e.target.value})}
                     data-testid="job-cocktails-allowed" />
            </div>
          </div>
          {/* Job Value */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Job Value (£)
            </label>
            <Input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.job_value}
              onChange={(e) => setFormData({...formData, job_value: e.target.value})}
            />
            <p className="text-xs text-gray-500 mt-1">Total value/price charged to client</p>
          </div>

          {/* Staff Costs Section */}
          <div className={`grid gap-4 ${['Cocktail Lesson', 'The Artistic Package', 'Late Night Package', 'The Ultimate Package'].includes(formData.job_type) ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Wage (£)
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.wage}
                onChange={(e) => setFormData({...formData, wage: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Travel (£)
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.travel_allowance}
                onChange={(e) => setFormData({...formData, travel_allowance: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Bonus (£)
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.bonus}
                onChange={(e) => setFormData({...formData, bonus: e.target.value})}
              />
            </div>
          </div>

          {/* Ingredients Cost - Only for Cocktail Jobs */}
          {['Cocktail Lesson', 'The Artistic Package', 'Late Night Package', 'The Ultimate Package'].includes(formData.job_type) && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Ingredients Cost (£)
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.ingredients_cost}
                onChange={(e) => setFormData({...formData, ingredients_cost: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">Cost of cocktail ingredients and supplies</p>
            </div>
          )}

          <div>
            <Input
              placeholder="Location"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              required
            />
          </div>

          <div>
            <Input
              placeholder="Equipment Needed"
              value={formData.equipment_needed}
              onChange={(e) => setFormData({...formData, equipment_needed: e.target.value})}
            />
          </div>

          {['Cocktail Lesson', 'The Artistic Package', 'Late Night Package', 'The Ultimate Package'].includes(formData.job_type) && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Select Cocktails (up to 4):</p>
              <div className="grid grid-cols-3 gap-2">
                {cocktailOptions.map((cocktail) => (
                  <Button
                    key={cocktail}
                    type="button"
                    variant={formData.cocktails_selected.includes(cocktail) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleCocktail(cocktail)}
                    disabled={!formData.cocktails_selected.includes(cocktail) && formData.cocktails_selected.length >= 4}
                    className="text-xs"
                  >
                    {cocktail}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Textarea
              placeholder="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
            />
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-yellow-500 hover:bg-yellow-600">
              {job ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const NotificationModal = ({ isOpen, onClose, onSend, staff }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    recipients: 'all'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSend(formData);
    setFormData({
      title: '',
      message: '',
      type: 'general',
      recipients: 'all'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Notification
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Title
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Notification title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Message
            </label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Notification message"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Type
            </label>
            <select 
              value={formData.type} 
              onChange={(e) => setFormData({...formData, type: e.target.value})}
              className="w-full"
            >
              <option value="">Select Type</option>
              <option value="general">General</option>
              <option value="job_available">Job Available</option>
              <option value="reminder">Reminder</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Recipients
            </label>
            <select 
              value={formData.recipients} 
              onChange={(e) => setFormData({...formData, recipients: e.target.value})}
              className="w-full"
            >
              <option value="">Select Recipients</option>
              <option value="all">All Staff</option>
              {(staff || []).map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-yellow-500 hover:bg-yellow-600">
              <Send className="w-4 h-4 mr-2" />
              Send Notification
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return <BrandLoading line="Just a moment…" testId="route-loading" />;
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.account_status === 'pending') {
    return <PendingApproval user={user} onLogout={logout} />;
  }
  return children;
};

// Admin Login Component (Secure)
const AdminDirectLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  // Debug info for mobile testing
  React.useEffect(() => {
    console.log("AdminDirectLogin mounted - current path:", window.location.pathname);
    console.log("User agent:", navigator.userAgent);
  }, []);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      // Verify it's an admin user
      if (response.data.user.role !== 'admin') {
        setError("Access denied. Admin credentials required.");
        setLoading(false);
        return;
      }

      login(response.data.token, response.data.user);
      navigate("/admin", { replace: true });
    } catch (error) {
      console.error("Admin login error:", error.response?.data);
      setError(error.response?.data?.detail || "Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BrandAuthLayout strapline="Admin portal" testId="admin-login-page">
      <form onSubmit={handleAdminLogin} className="space-y-4">
        <div>
          <h1 className="text-lg text-cream">Office sign in</h1>
          <p className="mt-1 text-sm text-cream/60" style={{ textTransform: 'none' }}>
            Manage jobs, staff, pay and profit.
          </p>
        </div>

        <div>
          <label htmlFor="admin-email" className="bd-label-dark">Admin email</label>
          <Input
            id="admin-email"
            type="email"
            placeholder="admin@butlerdirect.co.uk"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            className="h-12 bd-input-dark"
            data-testid="admin-email-input"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="bd-label-dark">Password</label>
          <Input
            id="admin-password"
            type="password"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            className="h-12 bd-input-dark"
            data-testid="admin-password-input"
          />
        </div>

        {error && (
          <p className="rounded-[10px] border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="admin-login-error">
            {error}
          </p>
        )}

        <Button type="submit" disabled={loading} size="lg" className="w-full" data-testid="admin-login-submit">
          {loading ? "Signing you in…" : "Sign in"}
        </Button>
      </form>

      <div className="mt-5 space-y-2 text-center">
        <button
          onClick={() => setShowForgotPassword(true)}
          className="block w-full text-sm text-cream/60 underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold"
          data-testid="admin-forgot-password-link"
        >
          Forgot password?
        </button>
        <button
          onClick={() => navigate("/login")}
          className="text-sm font-semibold text-gold transition-colors hover:text-cream"
          data-testid="admin-back-to-staff"
        >
          ← Back to the staff portal
        </button>
      </div>

      <ForgotPasswordDialog open={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
    </BrandAuthLayout>
  );
};

// Admin Protected Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <BrandLoading line="Just a moment…" testId="admin-route-loading" />;
  }

  if (!user) return <Navigate to="/admin-direct" replace />;
  if (user.role !== 'admin') return <Navigate to="/admin-direct" replace />;
  
  return children;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/rate/:token" element={<RatePage />} />
            <Route path="/admin-direct" element={<AdminDirectLogin />} />
            <Route path="/admin-login" element={<Navigate to="/admin-direct" replace />} />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;